/**
 * Page de détail d'une garantie (composant partagé)
 * Reprend la structure du récapitulatif (Step6Summary) avec édition par bloc
 */

import { useState, useMemo, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useWarranty, useUpdateWarranty, useDownloadWarrantyPdf, useUpdateWarrantyProvisionsById, usePropagateWarrantyDiagnostic, useBroadcastWarranty } from '@/routes/outil-pro/features/warranties/hooks/useWarranties';
import { useSociety } from '@/routes/outil-pro/features/societies/hooks/useSocieties';
import { useChildrenHaveStripe } from '@/features/societies/hooks/useSocieties';
import { useEffectiveConfiguration } from '@/features/societies/hooks/useSocietyConfiguration';
import { useAllowedDurations } from '@/features/warranties/hooks/useAllowedDurations';
import { usePartsTree } from '@/routes/outil-pro/features/warranties/hooks/useParts';
import { useWarrantyPermissions } from '@/routes/outil-pro/features/warranties/hooks/useWarrantyPermissions';
import { transformPartsTreeToCategories } from '@/routes/outil-pro/features/warranties/utils/transformPartsTree';
import { CategorySection } from '@/routes/outil-pro/features/warranties/components/parts-selection/CategorySection';
import {
  formatWarrantyType,
  formatVehicleType,
  formatDuration,
  formatPrice,
} from '@/routes/outil-pro/features/warranties/services/warrantyFormatter';
import { extractMonthlyFees, calculateMonthlyBreakdown } from '@/routes/outil-pro/features/warranties/services/pricingCalculator';

import { useNotifications } from '@/contexts/NotificationContext';
import { useAuth } from '@/hooks/auth/useAuth';
import { WarrantyAuditLogsCard } from '@/routes/outil-pro/features/warranties/components/WarrantyAuditLogsCard';
import { WarrantyPdfPreview } from '@/routes/outil-pro/features/warranties/components/WarrantyPdfPreview';
import { WarrantyGcCard } from '@/features/warranties/components/WarrantyGcCard';
import { FileText, Settings, Package, X, CheckCircle, AlertCircle, Loader2, Download, Search, Building2, Shield, Car, CreditCard, Clock, Gauge, Coins, ArrowLeftRight, Euro, RefreshCw, BookOpen, Eye, EyeOff, Siren } from 'lucide-react';
import { PresaleSheetPanel } from '@/features/presale-sheet/components/PresaleSheetPanel';
import { DetailHeader } from '@/Components/shared/DetailHeader';
import { InfoCard, InfoRow } from '@/Components/shared/InfoCard';
import { formatDate } from '@/utils/dateUtils';
import { FormInput } from '@/Components/shared/FormInput';
import { useAssistanceFormulas } from '@/features/assistance/hooks/useAssistance';
import { AssistanceFormulaPicker } from '@/features/assistance/components/AssistanceFormulaPicker';
import { InlineSelectInput } from '@/Components/shared/InlineSelectInput';
import { FormTextarea } from '@/Components/shared/FormTextarea';

import { useVendorWording } from '@/hooks/useVendorWording';
import { useWarrantyScopeSocietyId } from '../hooks/useWarrantyScopeSocietyId';

interface WarrantyDetailPageProps {
  warrantyId?: string;
  onBack?: () => void;
  className?: string;
  /** Mode embarqué : le Layout parent gère le padding et le fond */
  embedded?: boolean;
}

// Interface pour tracer les changements
interface FieldChange {
  field: string;
  label: string;
  oldValue: string;
  newValue: string;
}

// Labels des champs pour affichage (INFO labels sont dynamiques, voir useMemo dans le composant)

const FIELD_LABELS_CONFIG: Record<string, string> = {
  duration: 'Durée',
  financialCap: 'Plafond financier (€)',
  kmLimit: 'Limite kilométrique',
  provision: 'Provision (€)',
  headTransferPrice: 'Prix de rétrocession HT (€)',
  memberTransferPrice: 'Prix d\'achat HT (€)',
  commission: 'Commission vendeur (€)',
  assistanceFormulaId: "Offre d'assistance",
};

const FIELD_LABELS_PRICING: Record<string, string> = {
  sellingPrice: 'Prix de vente TTC (€)',
  servicePrice: 'Prix de service Affinicar HT (€)',
  paymentMode: 'Mode de paiement',
};

const PAYMENT_MODE_LABELS: Record<string, string> = {
  cash_only: 'Comptant',
  both: 'Comptant ou mensualisation',
  monthly_only: 'Mensualisation',
};

const getPaymentMode = (isCashPayment: boolean, isMensualisable: boolean): 'cash_only' | 'both' | 'monthly_only' => {
  if (isCashPayment && isMensualisable) return 'both';
  if (isMensualisable) return 'monthly_only';
  return 'cash_only';
};

const paymentModeToFlags = (mode: 'cash_only' | 'both' | 'monthly_only') => ({
  isCashPayment: mode === 'cash_only' || mode === 'both',
  isMensualisable: mode === 'both' || mode === 'monthly_only',
});


export const WarrantyDetailPage = ({ warrantyId: warrantyIdProp, onBack, className, embedded }: WarrantyDetailPageProps) => {
  const { id } = useParams<{ id: string }>();
  const effectiveId = warrantyIdProp || id;
  const navigate = useNavigate();
  const { showNotification } = useNotifications();
  const { user } = useAuth();
  const isExternalUser = user?.type === 'EXTERNAL';
  const handleBack = onBack || (() => navigate(-1));

  const { data: warranty, isLoading, error } = useWarranty(effectiveId);
  const { data: society } = useSociety(warranty?.societyId);
  // Config de la société pour laquelle l'EXTERNAL agit (celle de la garantie si
  // elle fait partie de ses stratégies, sinon sa principale) ou de la warranty (INTERNAL)
  const configSocietyId = useWarrantyScopeSocietyId(warranty?.societyId);
  const { data: config } = useEffectiveConfiguration(configSocietyId);

  // Référentiel des formules d'assistance. Appelé inconditionnellement : ce
  // composant a plusieurs `return` anticipés (chargement, erreur) et un hook placé
  // après l'un d'eux casserait l'ordre d'appel entre deux rendus.
  // Chargé aussi quand la garantie porte une formule alors que la société n'a plus
  // l'option : la désactivation n'est pas rétroactive, la garantie garde sa formule
  // et son supplément. Sans ce second cas, le libellé retomberait sur « Formule 3 »
  // alors que le montant, lui, resterait juste.
  const { data: assistanceFormulas } = useAssistanceFormulas(
    !!config?.assistanceEnabled || warranty?.assistanceFormulaId != null,
  );

  /** Libellé d'une formule, pour l'affichage et le diff de confirmation. */
  const assistanceLabelFor = useCallback(
    (formulaId: number | null | undefined): string => {
      if (formulaId == null) return 'Aucune';
      return (
        (assistanceFormulas || []).find((f) => f.id === formulaId)?.label
        ?? `Formule ${formulaId}`
      );
    },
    [assistanceFormulas],
  );
  const allowedDurations = useAllowedDurations(configSocietyId);
  const hasStripePayment = useMemo(() => (config?.monthlyPayment ?? 0) >= 1 || (config?.onlinePayment ?? 0) >= 1, [config?.monthlyPayment, config?.onlinePayment]);
  const isWarrantyPolicyOwner = useMemo(() => !config?.warrantyPolicyOwnerId || config?.warrantyPolicyOwnerId === configSocietyId, [config?.warrantyPolicyOwnerId, configSocietyId]);
  const { data: childrenStripeData } = useChildrenHaveStripe(
    configSocietyId,
    { enabled: !hasStripePayment && isWarrantyPolicyOwner },
  );
  const { data: partsTree } = usePartsTree('fr', warranty?.vehicleType);

  const permissions = useWarrantyPermissions(warranty?.societyId);
  const wording = useVendorWording(warranty?.societyId);
  const FIELD_LABELS_INFO = useMemo(() => ({
    name: `Nom de ${wording.withArticle}`,
    warrantyType: `Type ${wording.deType}`,
    diagnostic: 'Informations complémentaires',
  }), [wording]);
  const updateWarranty = useUpdateWarranty();
  const downloadPdf = useDownloadWarrantyPdf();
  const updateProvisions = useUpdateWarrantyProvisionsById();
  const propagateDiagnostic = usePropagateWarrantyDiagnostic();
  const broadcastWarranty = useBroadcastWarranty();
  const [showConfirmProvisions, setShowConfirmProvisions] = useState(false);
  const [showConfirmBroadcast, setShowConfirmBroadcast] = useState(false);
  const [showConfirmPropagateDiagnostic, setShowConfirmPropagateDiagnostic] = useState(false);
  const [errorPropagateDiagnostic, setErrorPropagateDiagnostic] = useState<string | null>(null);
  // Presale sheet panel : la config (showPrice, bannerColor, headerImage)
  // est entierement geree par <PresaleSheetPanel> via React Query.
  const [showPresalePanel, setShowPresalePanel] = useState(false);

  // Vérifier si l'utilisateur est interne (pour afficher les logs)
  const isInternalUser = useMemo(() => user?.type === 'INTERNAL', [user]);

  // États d'édition pour le bloc Informations
  const [editingInfo, setEditingInfo] = useState(false);
  const [showConfirmInfo, setShowConfirmInfo] = useState(false);
  const [errorInfo, setErrorInfo] = useState<string | null>(null);
  const [formDataInfo, setFormDataInfo] = useState({
    name: '',
    warrantyType: 0,
    diagnostic: '',
  });

  // États d'édition pour le bloc Configuration
  const [editingConfig, setEditingConfig] = useState(false);
  const [showConfirmConfig, setShowConfirmConfig] = useState(false);
  const [errorConfig, setErrorConfig] = useState<string | null>(null);
  // `provision` est volontairement `number | null` : une garantie sans provision
  // définie doit le rester. La forcer à 0 la ferait passer pour « définie à 0 »,
  // et la mise à jour des provisions écraserait alors ses contrats avec 0 au
  // lieu de les ignorer.
  const [formDataConfig, setFormDataConfig] = useState<{
    duration: number;
    financialCap: number;
    kmLimit: number;
    provision: number | null;
    headTransferPrice: number;
    memberTransferPrice: number;
    commission: number;
    assistanceFormulaId: number | null;
  }>({
    duration: 0,
    financialCap: 0,
    kmLimit: 0,
    provision: null,
    headTransferPrice: 0,
    memberTransferPrice: 0,
    commission: 0,
    assistanceFormulaId: null,
  });

  // États pour la commission (montant fixe ou pourcentage)
  const [commissionMode, setCommissionMode] = useState<'fixed' | 'percentage'>('fixed');
  const [commissionInputValue, setCommissionInputValue] = useState<number>(0);

  // Logique mutuelle exclusive Transfer Pricing
  const hasHeadTransferPrice = useMemo(() => {
    return (formDataConfig.headTransferPrice ?? 0) > 0;
  }, [formDataConfig.headTransferPrice]);

  const hasMemberTransferPrice = useMemo(() => {
    return (formDataConfig.memberTransferPrice ?? 0) > 0;
  }, [formDataConfig.memberTransferPrice]);

  // Reset automatique si les deux sont remplis
  useEffect(() => {
    if (hasHeadTransferPrice && hasMemberTransferPrice) {
      setFormDataConfig({ ...formDataConfig, memberTransferPrice: 0 });
    }
  }, [hasHeadTransferPrice, hasMemberTransferPrice]);

  // États d'édition pour le bloc Tarification
  const [editingPricing, setEditingPricing] = useState(false);
  const [showConfirmPricing, setShowConfirmPricing] = useState(false);
  const [errorPricing, setErrorPricing] = useState<string | null>(null);
  const [formDataPricing, setFormDataPricing] = useState({
    sellingPrice: 0,
    servicePrice: 0,
    paymentMode: 'cash_only' as 'cash_only' | 'both' | 'monthly_only',
  });


  // États d'édition pour le bloc Pièces Couvertes
  const [editingParts, setEditingParts] = useState(false);
  const [showConfirmParts, setShowConfirmParts] = useState(false);
  const [errorParts, setErrorParts] = useState<string | null>(null);
  const [formDataParts, setFormDataParts] = useState({
    selectedPartIds: [] as number[],
  });
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());

  // Déterminer l'option de paiement depuis les données warranty
  const paymentOption = useMemo(() => {
    if (!warranty) return null;
    if (warranty.isCashPayment && !warranty.isMensualisable) {
      return config?.onlinePayment ? 1 : 1;
    }
    if (warranty.isCashPayment && warranty.isMensualisable) {
      return 2;
    }
    if (!warranty.isCashPayment && warranty.isMensualisable) {
      return 3;
    }
    return null;
  }, [warranty, config?.onlinePayment]);

  // Calculs des détails de paiement selon la config de la société connectée
  const paymentDetails = useMemo(() => {
    if (!warranty || !config) return null;

    const onlinePlateformFees = config.onlinePlateformFees || 0;
    const monthlyPlateformFees = config.monthlyPlateformFees || 0;
    const hasOnlinePayment = config.onlinePayment === 2;
    const hasMonthlyPayment = config.monthlyPayment === 2;

    // Option 1 : Comptant uniquement
    if (warranty.isCashPayment && !warranty.isMensualisable) {
      if (hasOnlinePayment) {
        const fees = (warranty.sellingPrice * onlinePlateformFees) / 100;
        return {
          type: 'cash_only_online' as const,
          sellingPrice: warranty.sellingPrice,
          onlineFees: fees,
          onlinePlateformFeesPercent: onlinePlateformFees,
        };
      }
      return {
        type: 'cash_only_offline' as const,
        sellingPrice: warranty.sellingPrice,
      };
    }

    // Option 2 : Choix comptant/mensuel — adapter selon les capacités Stripe de la société
    if (warranty.isCashPayment && warranty.isMensualisable) {
      // Ni comptant Stripe ni mensuel : afficher le prix simple
      if (!hasOnlinePayment && !hasMonthlyPayment) {
        return {
          type: 'cash_only_offline' as const,
          sellingPrice: warranty.sellingPrice,
        };
      }

      // Comptant Stripe mais pas de mensuel : afficher comptant avec frais
      if (hasOnlinePayment && !hasMonthlyPayment) {
        const fees = (warranty.sellingPrice * onlinePlateformFees) / 100;
        return {
          type: 'cash_only_online' as const,
          sellingPrice: warranty.sellingPrice,
          onlineFees: fees,
          onlinePlateformFeesPercent: onlinePlateformFees,
        };
      }

      // Les deux actifs : afficher comptant + mensuel
      const cashFees = (warranty.sellingPrice * onlinePlateformFees) / 100;
      const breakdown = calculateMonthlyBreakdown(warranty.sellingPrice, warranty.duration, monthlyPlateformFees);

      // Mensuel mais pas de comptant Stripe : afficher les deux mais sans frais comptant
      return {
        type: 'both' as const,
        sellingPrice: warranty.sellingPrice,
        cashFees: hasOnlinePayment ? cashFees : 0,
        onlinePlateformFeesPercent: hasOnlinePayment ? onlinePlateformFees : 0,
        monthlyPriceWithFees: breakdown.monthlyClient,
        monthlyFees: breakdown.monthlyFee,
        monthlyPlateformFeesPercent: monthlyPlateformFees,
        duration: warranty.duration,
        hideOnlineFees: !hasOnlinePayment,
      };
    }

    // Option 3 : Mensuel uniquement — ne pas afficher si la société n'a pas le mensuel
    if (!warranty.isCashPayment && warranty.isMensualisable) {
      if (!hasMonthlyPayment) return null;

      const monthlyFees = extractMonthlyFees(warranty.sellingPrice, monthlyPlateformFees);
      return {
        type: 'monthly_only' as const,
        monthlyPrice: warranty.sellingPrice,
        monthlyFees,
        monthlyPlateformFeesPercent: monthlyPlateformFees,
        duration: warranty.duration,
      };
    }

    return null;
  }, [warranty, config]);

  // Transformer les pièces en catégories
  const categoriesWithSelection = useMemo(() => {
    if (!partsTree || !warranty?.parts) return [];
    const categories = transformPartsTreeToCategories(partsTree);
    const selectedPartIds = warranty.parts.map(p => p.id);
    
    return categories
      .map(category => {
        const selectedParts = category.parts.filter(part => 
          selectedPartIds.includes(part.id)
        );
        return {
          categoryLabel: category.label,
          selectedParts,
        };
      })
      .filter(category => category.selectedParts.length > 0);
  }, [partsTree, warranty?.parts]);

  const partsCounter = useMemo(() => {
    const totalCategories = categoriesWithSelection.length;
    const totalParts = categoriesWithSelection.reduce(
      (sum, cat) => sum + cat.selectedParts.length,
      0
    );
    return { totalCategories, totalParts };
  }, [categoriesWithSelection]);

  const paymentOptionLabel = useMemo(() => {
    if (paymentOption === 1) {
      return config?.onlinePayment ? 'Paiement au comptant Stripe' : 'Paiement comptant uniquement';
    }
    if (paymentOption === 2) {
      return 'Comptant ou mensualisation';
    }
    if (paymentOption === 3) {
      return 'Mensualisation uniquement';
    }
    return 'Aucune option';
  }, [paymentOption, config?.onlinePayment]);

  // Catégories complètes pour la modale d'édition des pièces
  const allCategories = useMemo(() => {
    return transformPartsTreeToCategories(partsTree);
  }, [partsTree]);

  // Calcul des changements pour le bloc Informations
  const infoChanges = useMemo((): FieldChange[] => {
    if (!warranty) return [];
    const changes: FieldChange[] = [];
    
    if (warranty.name !== formDataInfo.name) {
      changes.push({
        field: 'name',
        label: FIELD_LABELS_INFO.name,
        oldValue: warranty.name,
        newValue: formDataInfo.name,
      });
    }
    if (warranty.warrantyType !== formDataInfo.warrantyType) {
      changes.push({
        field: 'warrantyType',
        label: FIELD_LABELS_INFO.warrantyType,
        oldValue: formatWarrantyType(warranty.warrantyType),
        newValue: formatWarrantyType(formDataInfo.warrantyType),
      });
    }
    if (permissions.canViewDiagnostic && (warranty.diagnostic || '') !== formDataInfo.diagnostic) {
      changes.push({
        field: 'diagnostic',
        label: FIELD_LABELS_INFO.diagnostic,
        oldValue: warranty.diagnostic || '(vide)',
        newValue: formDataInfo.diagnostic || '(vide)',
      });
    }

    return changes;
  }, [formDataInfo, warranty]);

  // Calcul des changements pour le bloc Configuration
  const configChanges = useMemo((): FieldChange[] => {
    if (!warranty) return [];
    const changes: FieldChange[] = [];
    
    if (warranty.duration !== formDataConfig.duration) {
      changes.push({
        field: 'duration',
        label: FIELD_LABELS_CONFIG.duration,
        oldValue: formatDuration(warranty.duration),
        newValue: formatDuration(formDataConfig.duration),
      });
    }
    if (warranty.financialCap !== formDataConfig.financialCap) {
      changes.push({
        field: 'financialCap',
        label: FIELD_LABELS_CONFIG.financialCap,
        oldValue: formatPrice(warranty.financialCap),
        newValue: formatPrice(formDataConfig.financialCap),
      });
    }
    if (Number(warranty.kmLimit || 0) !== formDataConfig.kmLimit) {
      changes.push({
        field: 'kmLimit',
        label: FIELD_LABELS_CONFIG.kmLimit,
        oldValue: warranty.kmLimit ? `${warranty.kmLimit} km` : 'Non défini',
        newValue: formDataConfig.kmLimit ? `${formDataConfig.kmLimit} km` : 'Non défini',
      });
    }
    const currentProvision = warranty.provision ?? null;
    if (currentProvision !== formDataConfig.provision) {
      changes.push({
        field: 'provision',
        label: FIELD_LABELS_CONFIG.provision,
        oldValue: currentProvision === null ? 'Non définie' : formatPrice(currentProvision),
        newValue:
          formDataConfig.provision === null
            ? 'Non définie'
            : formatPrice(formDataConfig.provision),
      });
    }
    if (Number(warranty.headTransferPrice || 0) !== formDataConfig.headTransferPrice) {
      changes.push({
        field: 'headTransferPrice',
        label: FIELD_LABELS_CONFIG.headTransferPrice,
        oldValue: formatPrice(warranty.headTransferPrice || 0),
        newValue: formatPrice(formDataConfig.headTransferPrice),
      });
    }
    if (Number(warranty.memberTransferPrice || 0) !== formDataConfig.memberTransferPrice) {
      changes.push({
        field: 'memberTransferPrice',
        label: FIELD_LABELS_CONFIG.memberTransferPrice,
        oldValue: formatPrice(warranty.memberTransferPrice || 0),
        newValue: formatPrice(formDataConfig.memberTransferPrice),
      });
    }
    if ((warranty.assistanceFormulaId ?? null) !== formDataConfig.assistanceFormulaId) {
      changes.push({
        field: 'assistanceFormulaId',
        label: FIELD_LABELS_CONFIG.assistanceFormulaId,
        oldValue: assistanceLabelFor(warranty.assistanceFormulaId),
        newValue: assistanceLabelFor(formDataConfig.assistanceFormulaId),
      });
    }
    if (Number(warranty.commission || 0) !== formDataConfig.commission) {
      changes.push({
        field: 'commission',
        label: FIELD_LABELS_CONFIG.commission,
        oldValue: formatPrice(warranty.commission || 0),
        newValue: formatPrice(formDataConfig.commission),
      });
    }

    return changes;
  }, [formDataConfig, warranty, assistanceLabelFor]);

  // Calcul des changements pour le bloc Tarification
  const pricingChanges = useMemo((): FieldChange[] => {
    if (!warranty) return [];
    const changes: FieldChange[] = [];

    if (warranty.sellingPrice !== formDataPricing.sellingPrice) {
      changes.push({
        field: 'sellingPrice',
        label: FIELD_LABELS_PRICING.sellingPrice,
        oldValue: formatPrice(warranty.sellingPrice),
        newValue: formatPrice(formDataPricing.sellingPrice),
      });
    }
    if (isInternalUser && warranty.servicePrice !== formDataPricing.servicePrice) {
      changes.push({
        field: 'servicePrice',
        label: FIELD_LABELS_PRICING.servicePrice,
        oldValue: formatPrice(warranty.servicePrice),
        newValue: formatPrice(formDataPricing.servicePrice),
      });
    }
    const oldPaymentMode = getPaymentMode(warranty.isCashPayment, warranty.isMensualisable);
    if (oldPaymentMode !== formDataPricing.paymentMode) {
      changes.push({
        field: 'paymentMode',
        label: FIELD_LABELS_PRICING.paymentMode,
        oldValue: PAYMENT_MODE_LABELS[oldPaymentMode],
        newValue: PAYMENT_MODE_LABELS[formDataPricing.paymentMode],
      });
    }

    return changes;
  }, [formDataPricing, warranty]);


  // Calcul des changements pour le bloc Pièces Couvertes
  const partsChanges = useMemo(() => {
    if (!warranty || !warranty.parts || !partsTree) return { added: [] as string[], removed: [] as string[] };

    const currentPartIds = warranty.parts.map(p => p.id);
    const newPartIds = formDataParts.selectedPartIds;
    
    const added = newPartIds.filter(id => !currentPartIds.includes(id));
    const removed = currentPartIds.filter(id => !newPartIds.includes(id));
    
    const allPartsFlat = allCategories.flatMap(cat => cat.parts);
    
    return {
      added: added.map(id => allPartsFlat.find(p => p.id === id)?.partLabel || `Pièce #${id}`),
      removed: removed.map(id => allPartsFlat.find(p => p.id === id)?.partLabel || `Pièce #${id}`),
    };
  }, [formDataParts, warranty, partsTree, allCategories]);

  /**
   * Bascule la diffusion de la garantie, dans les deux sens.
   *
   * Non diffusée = absente de la liste, de la création de contrat, de l'onglet
   * Conditions de la société et du catalogue de l'API publique. C'est l'état
   * normal d'une garantie sur mesure créée pour un contrat précis, et c'est
   * aussi l'état dans lequel se retrouvaient les garanties issues d'un preset
   * avant le correctif — d'où la nécessité de pouvoir faire l'aller ET le
   * retour depuis cette page, qui est le seul point d'accès à une garantie non
   * diffusée.
   */
  const handleToggleBroadcast = async () => {
    if (!warranty) return;
    const next = !warranty.isBroadcast;
    try {
      await broadcastWarranty.mutateAsync({ id: warranty.uuid, broadcast: next });
      setShowConfirmBroadcast(false);
      showNotification(
        'success',
        next ? `${wording.singular} remise en diffusion` : `${wording.singular} retirée de la diffusion`,
      );
    } catch (error: any) {
      showNotification(
        'error',
        error?.response?.data?.message || 'Erreur lors du changement de diffusion',
      );
    }
  };

  // Handlers pour le bloc Informations
  const handleEditInfo = () => {
    if (warranty) {
      setFormDataInfo({
        name: warranty.name,
        warrantyType: warranty.warrantyType,
        diagnostic: warranty.diagnostic || '',
      });
      setEditingInfo(true);
      setErrorInfo(null);
    }
  };

  const handleSubmitInfo = () => {
    if (infoChanges.length === 0) {
      setEditingInfo(false);
      return;
    }
    setShowConfirmInfo(true);
  };

  const handleConfirmInfo = async () => {
    if (!effectiveId) return;
    try {
      setErrorInfo(null);
      await updateWarranty.mutateAsync({ id: effectiveId, warranty: formDataInfo });
      setEditingInfo(false);
      setShowConfirmInfo(false);
      showNotification('success', 'Informations mises à jour');
    } catch (error: any) {
      setErrorInfo(error.response?.data?.message || 'Erreur lors de la mise à jour');
    }
  };

  // Handlers pour le bloc Configuration
  const handleEditConfig = () => {
    if (warranty) {
      setFormDataConfig({
        duration: warranty.duration,
        financialCap: Number(warranty.financialCap),
        kmLimit: Number(warranty.kmLimit) || 0,
        provision: warranty.provision ?? null,
        headTransferPrice: Number(warranty.headTransferPrice) || 0,
        memberTransferPrice: Number(warranty.memberTransferPrice) || 0,
        commission: Number(warranty.commission) || 0,
        assistanceFormulaId: warranty.assistanceFormulaId ?? null,
      });
      setCommissionMode('fixed');
      setCommissionInputValue(Number(warranty.commission) || 0);
      setEditingConfig(true);
      setErrorConfig(null);
    }
  };

  const handleSubmitConfig = () => {
    if (configChanges.length === 0) {
      setEditingConfig(false);
      return;
    }
    setShowConfirmConfig(true);
  };

  const handleConfirmConfig = async () => {
    if (!effectiveId) return;
    try {
      setErrorConfig(null);
      // `provision` n'est envoyée que si elle a une valeur. Sinon on omet le
      // champ pour que l'API conserve le NULL existant (cf. commentaire sur
      // formDataConfig) — un envoi à 0 rendrait la garantie « définie à 0 ».
      const { provision, ...configWithoutProvision } = formDataConfig;
      const payload =
        provision === null ? configWithoutProvision : { ...configWithoutProvision, provision };
      await updateWarranty.mutateAsync({ id: effectiveId, warranty: payload });
      setEditingConfig(false);
      setShowConfirmConfig(false);
      showNotification('success', 'Configuration mise à jour');
    } catch (error: any) {
      setErrorConfig(error.response?.data?.message || 'Erreur lors de la mise à jour');
    }
  };

  // Handlers pour le bloc Tarification
  const handleEditPricing = () => {
    if (warranty) {
      setFormDataPricing({
        sellingPrice: Number(warranty.sellingPrice),
        servicePrice: Number(warranty.servicePrice),
        paymentMode: getPaymentMode(warranty.isCashPayment, warranty.isMensualisable),
      });
      setEditingPricing(true);
      setErrorPricing(null);
    }
  };

  const handleSubmitPricing = () => {
    if (pricingChanges.length === 0) {
      setEditingPricing(false);
      return;
    }
    setShowConfirmPricing(true);
  };

  const handleConfirmPricing = async () => {
    if (!effectiveId) return;
    try {
      setErrorPricing(null);
      const { paymentMode, sellingPrice, servicePrice } = formDataPricing;
      const pricingData = isInternalUser
        ? { sellingPrice, servicePrice, ...paymentModeToFlags(paymentMode) }
        : { sellingPrice, ...paymentModeToFlags(paymentMode) };
      await updateWarranty.mutateAsync({ id: effectiveId, warranty: pricingData });
      setEditingPricing(false);
      setShowConfirmPricing(false);
      showNotification('success', 'Tarification mise à jour');
    } catch (error: any) {
      setErrorPricing(error.response?.data?.message || 'Erreur lors de la mise à jour');
    }
  };


  // Handlers pour le bloc Pièces Couvertes
  const handleEditParts = () => {
    if (warranty && warranty.parts) {
      setFormDataParts({
        selectedPartIds: warranty.parts.map(p => p.id),
      });
      // Ouvrir toutes les catégories qui ont au moins une pièce sélectionnée
      const categoriesToExpand = warranty.parts ? allCategories
        .filter(cat => cat.parts.some(part => warranty.parts!.some(wp => wp.id === part.id)))
        .map(cat => cat.catKey) : [];
      setExpandedCategories(new Set(categoriesToExpand));
      setEditingParts(true);
      setErrorParts(null);
    }
  };

  const handleSubmitParts = () => {
    if (partsChanges.added.length === 0 && partsChanges.removed.length === 0) {
      setEditingParts(false);
      return;
    }
    setShowConfirmParts(true);
  };

  const handleConfirmParts = async () => {
    if (!effectiveId) return;
    try {
      setErrorParts(null);
      await updateWarranty.mutateAsync({
        id: effectiveId,
        warranty: { partIds: formDataParts.selectedPartIds }
      });
      setEditingParts(false);
      setShowConfirmParts(false);
      setExpandedCategories(new Set());
      showNotification('success', 'Pièces couvertes mises à jour');
    } catch (error: any) {
      setErrorParts(error.response?.data?.message || 'Erreur lors de la mise à jour');
    }
  };

  // Handlers pour la sélection des pièces dans la modale
  const handleCategoryToggle = (categoryKey: string) => {
    const category = allCategories.find(c => c.catKey === categoryKey);
    if (!category) return;

    const isCurrentlyExpanded = expandedCategories.has(categoryKey);
    const newExpanded = new Set(expandedCategories);

    if (isCurrentlyExpanded) {
      // Fermer et décocher toutes les pièces
      newExpanded.delete(categoryKey);
      const partIdsToRemove = category.parts.map(p => p.id);
      const newSelectedIds = formDataParts.selectedPartIds.filter(id => !partIdsToRemove.includes(id));
      setFormDataParts({ selectedPartIds: newSelectedIds });
    } else {
      // Ouvrir et cocher les pièces priorité 1
      newExpanded.add(categoryKey);
      const priority1Parts = category.parts.filter(p => p.selectionPriority === 1);
      const priority1Ids = priority1Parts.map(p => p.id);
      const newSelectedIds = [...new Set([...formDataParts.selectedPartIds, ...priority1Ids])];
      setFormDataParts({ selectedPartIds: newSelectedIds });
    }

    setExpandedCategories(newExpanded);
  };

  const handlePartToggle = (partId: number, priority: number) => {
    const isSelected = formDataParts.selectedPartIds.includes(partId);

    if (isSelected) {
      // Décocher - vérifier les permissions pour priorité 1
      if (priority === 1 && !permissions.canModifyDefaultParts) {
        showNotification('warning', "Vous ne pouvez pas décocher les pièces par défaut. Contactez votre commercial.");
        return;
      }
      const newSelectedIds = formDataParts.selectedPartIds.filter(id => id !== partId);
      setFormDataParts({ selectedPartIds: newSelectedIds });
    } else {
      // Cocher
      const newSelectedIds = [...formDataParts.selectedPartIds, partId];
      setFormDataParts({ selectedPartIds: newSelectedIds });
    }
  };

  const handleSelectAllPriority = (categoryKey: string, priority: number) => {
    const category = allCategories.find(c => c.catKey === categoryKey);
    if (!category) return;

    const priorityParts = category.parts.filter(p => p.selectionPriority === priority);
    const priorityIds = priorityParts.map(p => p.id);
    const allSelected = priorityIds.every(id => formDataParts.selectedPartIds.includes(id));

    if (allSelected) {
      // Décocher toutes
      if (priority === 1 && !permissions.canModifyDefaultParts) {
        showNotification('warning', "Vous ne pouvez pas décocher les pièces par défaut. Contactez votre commercial.");
        return;
      }
      const newSelectedIds = formDataParts.selectedPartIds.filter(id => !priorityIds.includes(id));
      setFormDataParts({ selectedPartIds: newSelectedIds });
    } else {
      // Cocher toutes
      const newSelectedIds = [...new Set([...formDataParts.selectedPartIds, ...priorityIds])];
      setFormDataParts({ selectedPartIds: newSelectedIds });
    }
  };

  // Search filter for parts
  const [partsSearch, setPartsSearch] = useState('');
  const filteredCategories = useMemo(() => {
    if (!partsSearch.trim()) return categoriesWithSelection;
    const q = partsSearch.toLowerCase();
    return categoriesWithSelection
      .map(cat => ({
        ...cat,
        selectedParts: cat.selectedParts.filter(p => p.label.toLowerCase().includes(q)),
      }))
      .filter(cat => cat.selectedParts.length > 0 || cat.categoryLabel.toLowerCase().includes(q));
  }, [categoriesWithSelection, partsSearch]);

  if (isLoading) {
    return (
      <div className={embedded ? (className || '') : `tw-min-h-screen tw-bg-gray-50 ${className || ''}`}>
        <div className="tw-px-6 tw-py-8 tw-space-y-4">
          {/* Skeleton header */}
          <div className="tw-h-6 tw-w-48 tw-bg-gray-200 tw-rounded tw-animate-pulse" />
          <div className="tw-h-28 tw-bg-gray-200 tw-rounded-xl tw-animate-pulse" />
          <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-4">
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="tw-h-40 tw-bg-gray-200 tw-rounded-xl tw-animate-pulse" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error || !warranty) {
    return (
      <div className={embedded ? (className || '') : `tw-min-h-screen tw-bg-gray-50 ${className || ''}`}>
        <div className="tw-px-6 tw-py-8">
          <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-py-16">
            <div className="tw-w-16 tw-h-16 tw-rounded-full tw-bg-red-100 tw-flex tw-items-center tw-justify-center tw-mb-4">
              <AlertCircle className="tw-w-8 tw-h-8 tw-text-red-500" />
            </div>
            <p className="tw-text-lg tw-font-semibold tw-text-gray-900 tw-mb-1">{wording.singular} introuvable</p>
            <p className="tw-text-sm tw-text-gray-500">Vérifiez l'identifiant ou revenez à la liste.</p>
            <button
              onClick={handleBack}
              className="tw-mt-4 tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-text-affinicar-primary tw-border tw-border-affinicar-primary tw-rounded-lg hover:tw-bg-affinicar-lighter tw-transition-colors"
            >
              Retour à la liste
            </button>
          </div>
        </div>
      </div>
    );
  }

  const warrantyTypeLabel = formatWarrantyType(warranty.warrantyType);
  const vehicleTypeLabel = formatVehicleType(warranty.vehicleType);
  const isExtension = warranty.warrantyType === 2;
  const durationLabel = isExtension ? 'Définie au contrat' : formatDuration(warranty.duration);

  // Couleur de la garantie (custom ou défaut)
  const primaryColor = warranty.color || '#8074af';
  const secondaryColor = warranty.color ? `${warranty.color}dd` : '#575fa7';


  const showTransferPricing = !!config?.isTransferPrice;
  // La société connectée doit être garante des contrats pour voir/éditer la provision.
  // - Pour un utilisateur externe : sa propre société doit être garante (guarantorId null ou = sa société)
  // - Pour un utilisateur interne (admin Affinicar) : pas de restriction
  const isConnectedSocietyGuarantor =
    !isExternalUser
    || !config?.guarantorId
    || config.guarantorId === configSocietyId;
  const showProvision = !!config?.defineProvision && isConnectedSocietyGuarantor;
  const showCommission = !!config?.commission;
  /**
   * L'assistance est une capacité du COMPTE vendeur : la ligne n'a de sens que si la
   * société a souscrit l'offre — sauf si la garantie en porte une. Un compte qui perd
   * l'option CONSERVE les formules de ses garanties, et les contrats souscrits
   * dessus restent facturés : masquer la ligne cacherait une assistance bien réelle.
   */
  const showAssistance = !!config?.assistanceEnabled || warranty?.assistanceFormulaId != null;
  const canMensualize = hasStripePayment || (isWarrantyPolicyOwner && !!childrenStripeData?.hasChildWithStripe);

  // Map warranty status to StatusVariant
  const badgeInfo = warranty.isActive
    ? { label: 'Active', variant: 'active' as const }
    : { label: 'Inactive', variant: 'expired' as const };

  return (
    <div className={embedded ? (className || '') : `tw-min-h-screen tw-bg-gray-50 ${className || ''}`}>
      {/* DetailHeader */}
      <div className={embedded ? 'tw-space-y-0' : 'tw-px-6 tw-pt-2'}>
        <DetailHeader
          type="warranty"
          title={warranty.name}
          status={badgeInfo}
          customGradient={{ from: primaryColor, to: secondaryColor }}
          breadcrumb={[
            onBack ? { label: wording.plural, onClick: onBack } : { label: wording.plural, href: '/warranties' },
            { label: warranty.name },
          ]}
          actions={
            (showProvision && permissions.canUpdateProvisions) || (isInternalUser && permissions.canEdit) ? (
              <div className="tw-flex tw-gap-2">
                {showProvision && permissions.canUpdateProvisions && (
                  <button
                    onClick={() => setShowConfirmProvisions(true)}
                    disabled={updateProvisions.isLoading}
                    className="tw-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1.5 tw-rounded-lg tw-text-sm tw-font-medium tw-text-white/90 hover:tw-text-white tw-bg-white/10 hover:tw-bg-white/20 tw-transition-colors disabled:tw-opacity-50"
                  >
                    {updateProvisions.isLoading ? (
                      <Loader2 className="tw-w-3.5 tw-h-3.5 tw-animate-spin" />
                    ) : (
                      <RefreshCw className="tw-w-3.5 tw-h-3.5" />
                    )}
                    Mettre à jour les provisions
                  </button>
                )}
                {/* Diffusion : un seul contrôle, dans les deux sens, pour qu'un
                    changement fait par erreur soit toujours réversible ici.
                    Réservé aux INTERNAL, par symétrie avec le chemin de retour :
                    seule la liste côté INTERNAL propose le filtre « Non
                    diffusées ». Un externe qui retirerait sa garantie de la
                    diffusion n'aurait plus aucun moyen de revenir sur cette
                    page pour annuler. */}
                {isInternalUser && permissions.canEdit && (
                  <button
                    onClick={() => setShowConfirmBroadcast(true)}
                    disabled={broadcastWarranty.isLoading}
                    className="tw-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1.5 tw-rounded-lg tw-text-sm tw-font-medium tw-text-white/90 hover:tw-text-white tw-bg-white/10 hover:tw-bg-white/20 tw-transition-colors disabled:tw-opacity-50"
                  >
                    {broadcastWarranty.isLoading ? (
                      <Loader2 className="tw-w-3.5 tw-h-3.5 tw-animate-spin" />
                    ) : warranty.isBroadcast ? (
                      <EyeOff className="tw-w-3.5 tw-h-3.5" />
                    ) : (
                      <Eye className="tw-w-3.5 tw-h-3.5" />
                    )}
                    {warranty.isBroadcast ? 'Retirer de la diffusion' : 'Remettre en diffusion'}
                  </button>
                )}
              </div>
            ) : undefined
          }
          headerContent={
            <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-x-5 tw-gap-y-2 lg:tw-border-l lg:tw-border-white/20 lg:tw-pl-5 tw-min-w-0">
              {/* Price */}
              <div className="tw-flex tw-flex-col">
                <span className="tw-text-[10px] tw-font-medium tw-text-white/60 tw-uppercase tw-tracking-wider">
                  {paymentDetails?.type === 'monthly_only' ? 'Prix mensuel TTC' : 'Prix TTC'}
                </span>
                <span className="tw-text-lg tw-font-bold tw-text-white">
                  {paymentDetails?.type === 'monthly_only'
                    ? `${formatPrice(paymentDetails.monthlyPrice)}${isExtension ? '' : ' /mois'}`
                    : formatPrice(warranty.sellingPrice)}
                </span>
              </div>
              <div className="tw-h-8 tw-w-px tw-bg-white/20" />
              {/* Code */}
              <div className="tw-flex tw-flex-col">
                <span className="tw-text-[10px] tw-font-medium tw-text-white/60 tw-uppercase tw-tracking-wider">Code</span>
                <span className="tw-text-sm tw-font-bold tw-font-mono tw-text-white">{warranty.code}</span>
              </div>
              <div className="tw-h-8 tw-w-px tw-bg-white/20" />
              {/* Duration */}
              <div className="tw-flex tw-flex-col">
                <span className="tw-text-[10px] tw-font-medium tw-text-white/60 tw-uppercase tw-tracking-wider">Durée</span>
                <span className="tw-text-sm tw-font-medium tw-text-white">{durationLabel}</span>
              </div>
              <div className="tw-h-8 tw-w-px tw-bg-white/20" />
              {/* Creation */}
              <div className="tw-flex tw-flex-col">
                <span className="tw-text-[10px] tw-font-medium tw-text-white/60 tw-uppercase tw-tracking-wider">Création</span>
                <span className="tw-text-sm tw-font-medium tw-text-white">
                  {warranty.createdAt ? formatDate(warranty.createdAt) : '—'}
                </span>
              </div>
            </div>
          }
        />
      </div>

      {/* Rappel factuel de l'état non diffusé : cette page est le seul point
          d'accès à une telle garantie, et rien ne le signale autrement.
          Volontairement neutre : c'est l'état normal d'une garantie sur mesure
          créée pour un contrat, pas nécessairement une anomalie. L'action est
          dans les boutons d'en-tête, pour n'avoir qu'un seul contrôle. */}
      {warranty.isBroadcast === false && (
        <div className={embedded ? 'tw-pt-4' : 'tw-px-6 tw-pt-4'}>
          <div className="tw-flex tw-items-start tw-gap-3 tw-rounded-lg tw-border tw-border-gray-300 tw-bg-gray-50 tw-p-4">
            <EyeOff className="tw-w-5 tw-h-5 tw-text-gray-500 tw-flex-shrink-0 tw-mt-0.5" />
            <div>
              <p className="tw-text-sm tw-font-semibold tw-text-gray-900 tw-mb-1">
                {wording.singular} non diffusée
              </p>
              <p className="tw-text-sm tw-text-gray-600 tw-mb-0">
                Elle n'apparaît pas dans la liste {wording.desPlural}, ni à la création de contrat,
                ni dans le catalogue de l'API publique — cette page est son seul point d'accès.
                C'est l'état attendu d'une garantie sur mesure créée pour un contrat précis.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Layout principal */}
      <div className={embedded ? 'tw-py-6' : 'tw-px-6 tw-py-6'}>
        <div className="tw-grid tw-grid-cols-1 lg:tw-grid-cols-12 tw-gap-4 tw-mb-4 lg:tw-items-stretch">
          {/* Colonne gauche (8 colonnes) */}
          <div className="lg:tw-col-span-8 tw-flex tw-flex-col tw-gap-4">
            {/* 2-column grid for info blocks */}
            <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-4">
              {/* Informations générales (merged Info + Payment) */}
              <InfoCard
                title="Informations générales"
                icon={<FileText className="tw-w-4 tw-h-4" />}
                editable={permissions.canEdit}
                onEdit={handleEditInfo}
              >
                {isExternalUser ? (
                  <InfoRow
                    label="Garantie"
                    icon={<Shield className="tw-w-3.5 tw-h-3.5" />}
                    value={warranty.name}
                  />
                ) : (
                  <InfoRow
                    label="Société"
                    icon={<Building2 className="tw-w-3.5 tw-h-3.5" />}
                    value={
                      society ? (
                        <Link
                          to={`/societies/${society.uuid}`}
                          className="tw-font-medium tw-no-underline tw-text-affinicar-primary hover:tw-underline"
                        >
                          {society.name}
                        </Link>
                      ) : 'Chargement...'
                    }
                  />
                )}
                <InfoRow label="Type" value={warrantyTypeLabel} icon={<Shield className="tw-w-3.5 tw-h-3.5" />} />
                <InfoRow label="Véhicule" value={vehicleTypeLabel} icon={<Car className="tw-w-3.5 tw-h-3.5" />} />
                {permissions.canViewDiagnostic && warranty.diagnostic && (
                  <div className="tw-mt-2 tw-pt-2 tw-border-t tw-border-gray-100">
                    <div className="tw-flex tw-items-center tw-justify-between tw-mb-1 tw-gap-2">
                      <p className="tw-text-xs tw-text-gray-500 tw-mb-0">Informations complémentaires</p>
                      <button
                        type="button"
                        onClick={() => {
                          setErrorPropagateDiagnostic(null);
                          setShowConfirmPropagateDiagnostic(true);
                        }}
                        className="tw-text-xs tw-text-affinicar-primary hover:tw-underline tw-bg-transparent tw-border-0 tw-p-0 tw-cursor-pointer"
                      >
                        Mettre à jour pour toutes les garanties
                      </button>
                    </div>
                    <div className="tw-text-xs tw-text-gray-800 tw-bg-gray-50 tw-p-2 tw-rounded tw-whitespace-pre-wrap tw-border tw-border-gray-200">
                      {warranty.diagnostic}
                    </div>
                  </div>
                )}
              </InfoCard>

              {/* Configuration */}
              <InfoCard
                title="Configuration"
                icon={<Settings className="tw-w-4 tw-h-4" />}
                editable={permissions.canEdit}
                onEdit={handleEditConfig}
              >
                <InfoRow label="Durée" value={durationLabel} icon={<Clock className="tw-w-3.5 tw-h-3.5" />} />
                <InfoRow label="Plafond" value={formatPrice(warranty.financialCap)} icon={<Euro className="tw-w-3.5 tw-h-3.5" />} />
                <InfoRow
                  label="Limite KM"
                  value={warranty.kmLimit ? `${warranty.kmLimit.toLocaleString('fr-FR')} km` : '—'}
                  hide={!warranty.kmLimit}
                  icon={<Gauge className="tw-w-3.5 tw-h-3.5" />}
                />
                {/* Seule ligne du bloc à s'afficher aussi quand la valeur est négative :
                    l'absence de ligne ne se lirait pas comme « désactivée ». */}
                <InfoRow
                  label="Assistance"
                  value={
                    <span
                      className={`tw-inline-flex tw-items-center tw-rounded-full tw-px-2 tw-py-0.5 tw-text-xs tw-font-medium ${
                        warranty.assistanceFormulaId != null
                          ? 'tw-bg-green-100 tw-text-green-800'
                          : 'tw-bg-gray-100 tw-text-gray-600'
                      }`}
                    >
                      {warranty.assistanceFormulaId != null
                        ? assistanceLabelFor(warranty.assistanceFormulaId)
                        : 'Désactivée'}
                    </span>
                  }
                  hide={!showAssistance}
                  icon={<Siren className="tw-w-3.5 tw-h-3.5" />}
                />
                <InfoRow
                  label="Provision"
                  value={formatPrice(warranty.provision ?? 0)}
                  hide={!showProvision || !(warranty.provision ?? 0)}
                  icon={<Coins className="tw-w-3.5 tw-h-3.5" />}
                />
                <InfoRow
                  label="Rétrocession HT"
                  value={formatPrice(warranty.headTransferPrice ?? 0)}
                  hide={!showTransferPricing || !(warranty.headTransferPrice ?? 0)}
                  icon={<ArrowLeftRight className="tw-w-3.5 tw-h-3.5" />}
                />
                <InfoRow
                  label="Commission"
                  value={formatPrice(warranty.commission ?? 0)}
                  hide={!showCommission || !(warranty.commission ?? 0)}
                  icon={<Euro className="tw-w-3.5 tw-h-3.5" />}
                />
              </InfoCard>
            </div>

            {/* Tarification — full width with improved layout */}
            <InfoCard
              title="Tarification"
              icon={<Euro className="tw-w-4 tw-h-4" />}
              editable={permissions.canEdit}
              onEdit={handleEditPricing}
            >
              {canMensualize && (
                <div className="tw-mb-3 tw-pb-3 tw-border-b tw-border-gray-100">
                  <InfoRow label="Mode de paiement" value={paymentOptionLabel} icon={<CreditCard className="tw-w-3.5 tw-h-3.5" />} />
                </div>
              )}
              {paymentDetails && (
                <div className="tw-space-y-3">
                  {/* Cash only offline */}
                  {paymentDetails.type === 'cash_only_offline' && (
                    <div className="tw-flex tw-items-baseline tw-gap-2">
                      <span className="tw-text-2xl tw-font-bold tw-text-gray-900">{formatPrice(paymentDetails.sellingPrice)}</span>
                      <span className="tw-text-xs tw-text-gray-500 tw-font-normal">TTC</span>
                    </div>
                  )}

                  {/* Cash only online */}
                  {paymentDetails.type === 'cash_only_online' && (
                    <div>
                      <div className="tw-flex tw-items-baseline tw-gap-2">
                        <span className="tw-text-2xl tw-font-bold tw-text-gray-900">{formatPrice(paymentDetails.sellingPrice)}</span>
                        <span className="tw-text-xs tw-text-gray-500 tw-font-normal">TTC</span>
                      </div>
                      <p className="tw-text-xs tw-text-gray-400 tw-mt-1 tw-mb-0">
                        dont {formatPrice(paymentDetails.onlineFees)} TTC ({paymentDetails.onlinePlateformFeesPercent}%) de frais de service
                      </p>
                    </div>
                  )}

                  {/* Both: Comptant + Mensuel */}
                  {paymentDetails.type === 'both' && (
                    <div className="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 tw-gap-4">
                      {/* Comptant */}
                      <div className="tw-bg-gray-50 tw-rounded-lg tw-p-3 tw-border tw-border-gray-100">
                        <p className="tw-text-xs tw-font-semibold tw-text-gray-500 tw-uppercase tw-tracking-wider tw-mb-1">Comptant</p>
                        <div className="tw-flex tw-items-baseline tw-gap-1.5">
                          <span className="tw-text-xl tw-font-bold tw-text-gray-900">{formatPrice(paymentDetails.sellingPrice)}</span>
                          <span className="tw-text-xs tw-text-gray-500">TTC</span>
                        </div>
                        {!paymentDetails.hideOnlineFees && (
                          <p className="tw-text-xs tw-text-gray-400 tw-mt-1 tw-mb-0">
                            dont {formatPrice(paymentDetails.cashFees)} ({paymentDetails.onlinePlateformFeesPercent}%) frais
                          </p>
                        )}
                      </div>
                      {/* Mensualisé */}
                      <div className="tw-bg-gray-50 tw-rounded-lg tw-p-3 tw-border tw-border-gray-100">
                        <p className="tw-text-xs tw-font-semibold tw-text-gray-500 tw-uppercase tw-tracking-wider tw-mb-1">Mensualisé</p>
                        <div className="tw-flex tw-items-baseline tw-gap-1.5">
                          <span className="tw-text-xl tw-font-bold tw-text-gray-900">{formatPrice(paymentDetails.monthlyPriceWithFees)}</span>
                          <span className="tw-text-xs tw-text-gray-500">{isExtension ? 'TTC' : `TTC /mois x ${paymentDetails.duration}`}</span>
                        </div>
                        <p className="tw-text-xs tw-text-gray-400 tw-mt-1 tw-mb-0">
                          dont {formatPrice(paymentDetails.monthlyFees)} ({paymentDetails.monthlyPlateformFeesPercent}%) frais{isExtension ? '' : ' /mois'}
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Monthly only */}
                  {paymentDetails.type === 'monthly_only' && (
                    <div>
                      <div className="tw-flex tw-items-baseline tw-gap-2">
                        <span className="tw-text-2xl tw-font-bold tw-text-gray-900">{formatPrice(paymentDetails.monthlyPrice)}</span>
                        <span className="tw-text-xs tw-text-gray-500 tw-font-normal">{isExtension ? 'TTC' : `TTC /mois x ${paymentDetails.duration} mois`}</span>
                      </div>
                      <p className="tw-text-xs tw-text-gray-400 tw-mt-1 tw-mb-0">
                        dont {formatPrice(paymentDetails.monthlyFees)} TTC ({paymentDetails.monthlyPlateformFeesPercent}%) de frais{isExtension ? '' : ' /mois'}
                      </p>
                    </div>
                  )}

                  {/* Prix d'achat HT */}
                  {showTransferPricing && (warranty.memberTransferPrice ?? 0) > 0 && (
                    <div className="tw-pt-3 tw-border-t tw-border-gray-100">
                      <InfoRow label="Prix d'achat HT" value={formatPrice(warranty.memberTransferPrice ?? 0)} icon={<Euro className="tw-w-3.5 tw-h-3.5" />} />
                    </div>
                  )}

                  {/* Ce qu'Affinicar facture au vendeur — réservé aux internes.
                      Le prix de prestation était jusqu'ici modifiable par un interne
                      sans jamais être affiché sur la fiche ; il l'est désormais.

                      `servicePrice` et `assistancePrice` sont DEUX colonnes distinctes :
                      le supplément d'assistance s'AJOUTE au prix de prestation, il ne le
                      remplace pas. D'où la ligne de total, qui est le montant réellement
                      facturé — et qui évite de laisser faire l'addition de tête. */}
                  {isInternalUser && (
                    <div className="tw-pt-3 tw-border-t tw-border-gray-100 tw-space-y-1">
                      <InfoRow
                        label="Prestation Affinicar HT"
                        value={formatPrice(warranty.servicePrice ?? 0)}
                        icon={<Euro className="tw-w-3.5 tw-h-3.5" />}
                      />
                      {warranty.assistanceFormulaId != null && (
                        <>
                          <InfoRow
                            label={`Assistance — ${assistanceLabelFor(warranty.assistanceFormulaId)}`}
                            value={formatPrice(warranty.assistancePrice ?? 0)}
                            icon={<Siren className="tw-w-3.5 tw-h-3.5" />}
                          />
                          <div className="tw-flex tw-items-center tw-justify-between tw-pt-2 tw-border-t tw-border-gray-100">
                            <span className="tw-text-xs tw-font-semibold tw-text-gray-700">
                              Total prestation HT
                            </span>
                            <span className="tw-text-sm tw-font-bold tw-text-gray-900">
                              {formatPrice((warranty.servicePrice ?? 0) + (warranty.assistancePrice ?? 0))}
                            </span>
                          </div>
                        </>
                      )}
                    </div>
                  )}
                </div>
              )}
            </InfoCard>

            {/* Pièces Couvertes — with search */}
            <InfoCard
              title="Pièces couvertes"
              icon={<Package className="tw-w-4 tw-h-4" />}
              editable={permissions.canEdit}
              onEdit={handleEditParts}
              className="tw-flex tw-flex-col"
            >
              <div className="tw-flex tw-flex-col sm:tw-flex-row tw-items-start sm:tw-items-center tw-justify-between tw-gap-2 tw-mb-3">
                <p className="tw-text-xs tw-text-gray-500 tw-mb-0">
                  {partsCounter.totalCategories} catégorie{partsCounter.totalCategories > 1 ? 's' : ''} &bull; {partsCounter.totalParts} pièce{partsCounter.totalParts > 1 ? 's' : ''}
                </p>
                {categoriesWithSelection.length > 0 && (
                  <div className="tw-relative tw-w-full sm:tw-w-56">
                    <Search className="tw-absolute tw-left-2.5 tw-top-1/2 tw--translate-y-1/2 tw-w-3.5 tw-h-3.5 tw-text-gray-400" />
                    <input
                      type="text"
                      placeholder="Rechercher une pièce..."
                      value={partsSearch}
                      onChange={(e) => setPartsSearch(e.target.value)}
                      className="tw-w-full tw-pl-8 tw-pr-3 tw-py-1.5 tw-text-xs tw-border tw-border-gray-200 tw-rounded-lg focus:tw-outline-none focus:tw-ring-1 focus:tw-ring-affinicar-primary/50 focus:tw-border-affinicar-primary"
                    />
                  </div>
                )}
              </div>

              {filteredCategories.length > 0 ? (
                <div className="tw-max-h-96 tw-overflow-y-auto tw-space-y-3">
                  {filteredCategories.map((category) => (
                    <div key={category.categoryLabel}>
                      <h4 className="tw-font-bold tw-text-gray-700 tw-text-xs tw-uppercase tw-tracking-wider tw-mb-2 tw-flex tw-items-center tw-gap-2">
                        {category.categoryLabel}
                        <span className="tw-text-gray-400 tw-font-normal">({category.selectedParts.length})</span>
                      </h4>
                      <div className="tw-flex tw-flex-wrap tw-gap-1.5">
                        {category.selectedParts.map((part) => (
                          <span
                            key={part.id}
                            className="tw-px-2.5 tw-py-1 tw-rounded-full tw-text-xs tw-font-medium tw-border tw-bg-white tw-text-gray-700 tw-border-gray-200 hover:tw-border-gray-300 tw-transition-colors"
                          >
                            {part.label}
                          </span>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              ) : partsSearch ? (
                <p className="tw-text-sm tw-text-gray-400 tw-italic tw-text-center tw-py-4 tw-mb-0">
                  Aucune pièce ne correspond à "{partsSearch}"
                </p>
              ) : (
                <p className="tw-text-sm tw-text-gray-400 tw-italic tw-mb-0">Aucune pièce sélectionnée</p>
              )}
            </InfoCard>

            {/* Conditions Générales — assignation par variante (cash/stripe).
                Visible uniquement pour les utilisateurs INTERNAL. */}
            {isInternalUser && (
              <WarrantyGcCard
                warranty={warranty}
                hasStripePayment={hasStripePayment}
                canEdit={permissions.canEdit}
              />
            )}
          </div>

          {/* Colonne droite - Preview PDF (4 colonnes) */}
          <div className="lg:tw-col-span-4">
            <div className="lg:tw-sticky lg:tw-top-4">
              {config?.presaleSheet === 1 && (
                <button
                  onClick={() => setShowPresalePanel(true)}
                  className="tw-flex tw-items-center tw-justify-center tw-gap-2 tw-w-full tw-px-4 tw-py-2.5 tw-mb-4 tw-rounded-lg tw-text-sm tw-font-medium tw-text-affinicar-primary tw-border tw-border-affinicar-primary hover:tw-bg-affinicar-lighter tw-transition-colors"
                >
                  <BookOpen className="tw-w-4 tw-h-4" />
                  Brochure avant vente
                </button>
              )}
              <WarrantyPdfPreview
                warrantyUuid={warranty.uuid}
                warrantyName={warranty.name}
                headerAction={permissions.canDownloadCgp ? (
                  <button
                    onClick={() => {
                      downloadPdf.mutate(warranty.uuid, {
                        onSuccess: () => showNotification('success', 'PDF téléchargé avec succès'),
                        onError: (err: any) => showNotification('error', err?.message || 'Erreur lors du téléchargement du PDF'),
                      });
                    }}
                    disabled={downloadPdf.isLoading}
                    className="tw-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1.5 tw-rounded-lg tw-text-sm tw-font-medium tw-text-affinicar-primary hover:tw-text-affinicar-dark hover:tw-bg-affinicar-lighter tw-transition-colors disabled:tw-opacity-50 disabled:tw-cursor-not-allowed"
                    title="Télécharger CGP"
                  >
                    {downloadPdf.isLoading ? (
                      <Loader2 className="tw-w-3.5 tw-h-3.5 tw-animate-spin" />
                    ) : (
                      <Download className="tw-w-3.5 tw-h-3.5" />
                    )}
                    <span>Télécharger</span>
                  </button>
                ) : undefined}
              />
            </div>
          </div>
        </div>

        {/* Historique des modifications */}
        {isInternalUser && warranty && (
          <div className="tw-mt-4">
            <WarrantyAuditLogsCard warrantyUuid={warranty.uuid} />
          </div>
        )}
      </div>

      {/* Modales d'édition et de confirmation */}
      {/*
        Panneaux bornés en hauteur + corps scrollable : sans ça, un contenu plus
        haut que la fenêtre déborde en haut ET en bas (overlay centré) et le bouton
        « Enregistrer » devient inatteignable. overscroll-contain évite que la molette
        reprenne sur la page de fond une fois le bas de la modale atteint.
        La double borne vh/dvh suit WizardLayout : sur iOS, vh ignore la barre d'adresse.
      */}
      
      {/* Modal Édition Informations */}
      {editingInfo && (
        <div className="tw-fixed tw-inset-0 tw-bg-black tw-bg-opacity-50 tw-flex tw-items-center tw-justify-center tw-z-50">
          <div className="tw-bg-white tw-rounded-lg tw-shadow-xl tw-max-w-2xl tw-w-full tw-m-4 tw-max-h-[90vh] tw-max-h-[90dvh] tw-flex tw-flex-col">
            <div className="tw-flex tw-items-center tw-justify-between tw-p-6 tw-border-b tw-flex-shrink-0">
              <h3 className="tw-text-xl tw-font-semibold tw-text-gray-900">Modifier les informations</h3>
              <button
                onClick={() => setEditingInfo(false)}
                className="tw-text-gray-400 hover:tw-text-gray-600"
              >
                <X className="tw-w-5 tw-h-5" />
              </button>
            </div>

            <div className="tw-p-6 tw-space-y-4 tw-overflow-y-auto tw-flex-1 tw-overscroll-contain">
              <FormInput
                label={`Nom de ${wording.withArticle}`}
                value={formDataInfo.name}
                onChange={(value) => setFormDataInfo({ ...formDataInfo, name: value })}
                required
              />

              <InlineSelectInput
                label={`Type ${wording.deType}`}
                name="warrantyType"
                options={[
                  { label: 'Garantie', value: '0' },
                  { label: 'Prestation', value: '1' },
                ]}
                value={formDataInfo.warrantyType.toString()}
                onChange={(value) => setFormDataInfo({ ...formDataInfo, warrantyType: Number(value) })}
                required
                align="left"
              />

              {permissions.canViewDiagnostic && (
                <FormTextarea
                  label="Informations complémentaires"
                  value={formDataInfo.diagnostic}
                  onChange={(value) => setFormDataInfo({ ...formDataInfo, diagnostic: value })}
                  rows={4}
                  placeholder="Informations complémentaires"
                />
              )}
            </div>

            <div className="tw-flex tw-items-center tw-justify-end tw-gap-3 tw-p-6 tw-border-t tw-flex-shrink-0">
              <button
                onClick={() => setEditingInfo(false)}
                className="tw-px-4 tw-py-2 tw-text-gray-700 tw-bg-gray-100 tw-rounded-md hover:tw-bg-gray-200 tw-transition-colors"
              >
                Annuler
              </button>
              <button
                onClick={handleSubmitInfo}
                className="tw-px-4 tw-py-2 tw-text-white tw-rounded-md tw-transition-colors"
                style={{ backgroundColor: primaryColor }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.opacity = '0.9';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.opacity = '1';
                }}
              >
                Enregistrer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Édition Configuration */}
      {editingConfig && (
        <div className="tw-fixed tw-inset-0 tw-bg-black tw-bg-opacity-50 tw-flex tw-items-center tw-justify-center tw-z-50">
          <div className="tw-bg-white tw-rounded-lg tw-shadow-xl tw-max-w-2xl tw-w-full tw-m-4 tw-max-h-[90vh] tw-max-h-[90dvh] tw-flex tw-flex-col">
            <div className="tw-flex tw-items-center tw-justify-between tw-p-6 tw-border-b tw-flex-shrink-0">
              <h3 className="tw-text-xl tw-font-semibold tw-text-gray-900">Modifier la configuration</h3>
              <button
                onClick={() => setEditingConfig(false)}
                className="tw-text-gray-400 hover:tw-text-gray-600"
              >
                <X className="tw-w-5 tw-h-5" />
              </button>
            </div>

            <div className="tw-p-6 tw-space-y-4 tw-overflow-y-auto tw-flex-1 tw-overscroll-contain">
              <InlineSelectInput
                label="Durée"
                name="duration"
                options={allowedDurations.map((d) => ({ label: `${d} mois`, value: d.toString() }))}
                value={formDataConfig.duration.toString()}
                onChange={(value) => setFormDataConfig({ ...formDataConfig, duration: Number(value) })}
                required
                align="left"
              />

              <FormInput
                label="Plafond financier (€)"
                type="number"
                value={formDataConfig.financialCap.toString()}
                onChange={(value) => setFormDataConfig({ ...formDataConfig, financialCap: Number(value) })}
                required
              />

              <FormInput
                label="Limite kilométrique (km)"
                type="number"
                value={formDataConfig.kmLimit.toString()}
                onChange={(value) => setFormDataConfig({ ...formDataConfig, kmLimit: Number(value) })}
                required
              />

              {/*
                Même composant qu'à la création : interrupteur, cartes de formules,
                tarif et prestations. Il porte aussi le contrôle de durée éligible,
                absent de l'ancienne liste déroulante — on pouvait ramener une
                garantie à 3 mois en gardant sa formule, et n'être arrêté qu'au 400
                du serveur.
              */}
              <AssistanceFormulaPicker
                mode="edit"
                persistedFormulaId={warranty.assistanceFormulaId ?? null}
                societyId={configSocietyId}
                duration={formDataConfig.duration}
                assistanceEnabled={config?.assistanceEnabled}
                value={formDataConfig.assistanceFormulaId}
                onChange={(assistanceFormulaId) =>
                  setFormDataConfig((current) => ({ ...current, assistanceFormulaId }))
                }
              />

              {showProvision && (
                <FormInput
                  label="Provision (€)"
                  type="number"
                  value={formDataConfig.provision?.toString() ?? ''}
                  onChange={(value) =>
                    setFormDataConfig({
                      ...formDataConfig,
                      provision: value === '' ? null : Number(value),
                    })
                  }
                  required
                />
              )}

              {showTransferPricing && (
                <div className="tw-grid tw-grid-cols-2 tw-gap-4">
                  <div>
                    <FormInput
                      label="Prix de rétrocession HT (€)"
                      type="number"
                      value={formDataConfig.headTransferPrice.toString()}
                      onChange={(value) => setFormDataConfig({ ...formDataConfig, headTransferPrice: Number(value) })}
                      disabled={hasMemberTransferPrice}
                    />
                    {hasMemberTransferPrice && (
                      <p className="tw-text-xs tw-text-amber-600 tw-mt-1">
                        Désactivé car Prix d'achat est rempli
                      </p>
                    )}
                  </div>

                  <div>
                    <FormInput
                      label="Prix d'achat HT (€)"
                      type="number"
                      value={formDataConfig.memberTransferPrice.toString()}
                      onChange={(value) => setFormDataConfig({ ...formDataConfig, memberTransferPrice: Number(value) })}
                      disabled={hasHeadTransferPrice}
                    />
                    {hasHeadTransferPrice && (
                      <p className="tw-text-xs tw-text-amber-600 tw-mt-1 tw-mb-0">
                        Désactivé car Prix de rétrocession est rempli
                      </p>
                    )}
                  </div>
                </div>
              )}

              {showCommission && (
                <div>
                  <div className="tw-relative">
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      max={commissionMode === 'percentage' ? 100 : undefined}
                      value={commissionInputValue || ''}
                      onChange={(e) => {
                        const numValue = parseFloat(e.target.value) || 0;
                        setCommissionInputValue(numValue);
                        const priceTTC = Number(warranty?.sellingPrice) || 0;
                        if (commissionMode === 'percentage' && priceTTC > 0) {
                          setFormDataConfig({ ...formDataConfig, commission: (priceTTC * numValue) / 100 });
                        } else {
                          setFormDataConfig({ ...formDataConfig, commission: numValue });
                        }
                      }}
                      placeholder=" "
                      className="tw-w-full tw-pl-3 tw-pr-[160px] tw-pt-5 tw-pb-1.5 tw-text-base tw-border tw-rounded-xl tw-transition-all tw-peer tw-placeholder-transparent tw-border-gray-200 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-affinicar-primary/20 focus:tw-border-affinicar-primary"
                    />
                    <label
                      className={`tw-absolute tw-left-3 tw-pointer-events-none tw-transition-all tw-duration-200 tw-origin-left
                        ${commissionInputValue
                          ? 'tw-top-1.5 tw-text-[11px] tw-text-affinicar-primary'
                          : 'tw-top-[13px] tw-text-base tw-text-gray-400 peer-focus:tw-top-1.5 peer-focus:tw-text-[11px] peer-focus:tw-text-affinicar-primary'
                        }`}
                    >
                      Commission vendeur
                    </label>

                    <div className="tw-absolute tw-right-2 tw-top-1/2 -tw-translate-y-1/2 tw-flex tw-items-center tw-bg-gray-100 tw-rounded-lg tw-p-0.5">
                      <button
                        type="button"
                        onClick={() => {
                          setCommissionMode('percentage');
                          const priceTTC = Number(warranty?.sellingPrice) || 0;
                          if (priceTTC > 0 && commissionInputValue) {
                            setFormDataConfig({ ...formDataConfig, commission: (priceTTC * commissionInputValue) / 100 });
                          }
                        }}
                        className={`tw-px-3 tw-py-1 tw-text-xs tw-font-semibold tw-rounded-md tw-transition-all ${
                          commissionMode === 'percentage'
                            ? 'tw-bg-white tw-text-affinicar-primary tw-shadow-sm'
                            : 'tw-text-gray-500 hover:tw-text-gray-700'
                        }`}
                      >
                        %
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setCommissionMode('fixed');
                          if (commissionInputValue) {
                            setFormDataConfig({ ...formDataConfig, commission: commissionInputValue });
                          }
                        }}
                        className={`tw-px-3 tw-py-1 tw-text-xs tw-font-semibold tw-rounded-md tw-transition-all ${
                          commissionMode === 'fixed'
                            ? 'tw-bg-white tw-text-affinicar-primary tw-shadow-sm'
                            : 'tw-text-gray-500 hover:tw-text-gray-700'
                        }`}
                      >
                        €
                      </button>
                    </div>
                  </div>
                  {commissionMode === 'percentage' && formDataConfig.commission > 0 && (
                    <p className="tw-text-xs tw-text-affinicar-primary tw-mt-1 tw-mb-0">
                      Montant calculé : {formDataConfig.commission.toFixed(2)} € TTC
                    </p>
                  )}
                </div>
              )}
            </div>

            <div className="tw-flex tw-items-center tw-justify-end tw-gap-3 tw-p-6 tw-border-t tw-flex-shrink-0">
              <button
                onClick={() => setEditingConfig(false)}
                className="tw-px-4 tw-py-2 tw-text-gray-700 tw-bg-gray-100 tw-rounded-md hover:tw-bg-gray-200 tw-transition-colors"
              >
                Annuler
              </button>
              <button
                onClick={handleSubmitConfig}
                className="tw-px-4 tw-py-2 tw-text-white tw-rounded-md tw-transition-colors"
                style={{ backgroundColor: primaryColor }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.opacity = '0.9';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.opacity = '1';
                }}
              >
                Enregistrer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Confirmation Configuration */}
      {showConfirmConfig && (
        <div className="tw-fixed tw-inset-0 tw-bg-black tw-bg-opacity-50 tw-flex tw-items-center tw-justify-center tw-z-[101] tw-p-4">
          <div className="tw-bg-white tw-rounded-lg tw-shadow-xl tw-max-w-2xl tw-w-full tw-max-h-[80vh] tw-overflow-y-auto">
            <div className="tw-flex tw-items-center tw-justify-between tw-p-6 tw-border-b tw-border-gray-200">
              <div className="tw-flex tw-items-center tw-gap-3">
                <div className="tw-w-10 tw-h-10 tw-bg-affinicar-lighter tw-rounded-full tw-flex tw-items-center tw-justify-center">
                  <CheckCircle className="tw-w-6 tw-h-6 tw-text-affinicar-primary" />
                </div>
                <h2 className="tw-text-xl tw-font-bold tw-text-gray-900">
                  Confirmer les modifications
                </h2>
              </div>
              <button
                onClick={() => setShowConfirmConfig(false)}
                disabled={updateWarranty.isLoading}
                className="tw-text-gray-400 hover:tw-text-gray-600 tw-transition-colors disabled:tw-opacity-50"
              >
                <X className="tw-w-5 tw-h-5" />
              </button>
            </div>

            <div className="tw-p-6">
              <p className="tw-text-sm tw-text-gray-600 tw-mb-4">
                Vous êtes sur le point de modifier la configuration suivante :
              </p>

              <div className="tw-space-y-3">
                {configChanges.map((change, index) => (
                  <div
                    key={index}
                    className="tw-bg-gray-50 tw-border tw-border-gray-200 tw-rounded-lg tw-p-4"
                  >
                    <div className="tw-flex tw-items-start tw-gap-3">
                      <div className="tw-flex-1">
                        <p className="tw-text-sm tw-font-semibold tw-text-gray-900 tw-mb-2">
                          {change.label}
                        </p>
                        <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-3">
                          <div>
                            <p className="tw-text-xs tw-font-medium tw-text-gray-500 tw-mb-1">
                              Ancienne valeur :
                            </p>
                            <p className="tw-text-sm tw-text-gray-700 tw-bg-white tw-p-2 tw-rounded tw-border tw-border-gray-200">
                              {change.oldValue}
                            </p>
                          </div>
                          <div>
                            <p className="tw-text-xs tw-font-medium tw-text-green-600 tw-mb-1">
                              Nouvelle valeur :
                            </p>
                            <p className="tw-text-sm tw-text-green-700 tw-bg-green-50 tw-p-2 tw-rounded tw-border tw-border-green-200 tw-font-medium">
                              {change.newValue}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {errorConfig && (
                <div className="tw-bg-red-50 tw-border tw-border-red-200 tw-rounded-lg tw-p-4 tw-mt-4">
                  <div className="tw-flex tw-items-start tw-gap-3">
                    <AlertCircle className="tw-w-5 tw-h-5 tw-text-red-600 tw-flex-shrink-0 tw-mt-0.5" />
                    <p className="tw-text-sm tw-text-red-800">{errorConfig}</p>
                  </div>
                </div>
              )}
            </div>

            <div className="tw-flex tw-justify-end tw-gap-3 tw-p-6 tw-border-t tw-border-gray-200">
              <button
                type="button"
                onClick={() => setShowConfirmConfig(false)}
                disabled={updateWarranty.isLoading}
                className="tw-px-6 tw-py-2.5 tw-bg-gray-200 tw-text-gray-700 tw-font-medium tw-rounded-lg hover:tw-bg-gray-300 tw-transition-colors disabled:tw-opacity-50 disabled:tw-cursor-not-allowed"
              >
                Retour
              </button>
              <button
                type="button"
                onClick={handleConfirmConfig}
                disabled={updateWarranty.isLoading}
                className="tw-flex tw-items-center tw-gap-2 tw-px-6 tw-py-2.5 tw-text-white tw-font-medium tw-rounded-lg tw-transition-colors disabled:tw-opacity-50 disabled:tw-cursor-not-allowed"
                style={{ backgroundColor: primaryColor }}
                onMouseEnter={(e) => {
                  if (!updateWarranty.isLoading) {
                    e.currentTarget.style.opacity = '0.9';
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.opacity = '1';
                }}
              >
                {updateWarranty.isLoading ? (
                  <>
                    <Loader2 className="tw-w-4 tw-h-4 tw-animate-spin" />
                    Enregistrement...
                  </>
                ) : (
                  'Confirmer et enregistrer'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Édition Tarification */}
      {editingPricing && (
        <div className="tw-fixed tw-inset-0 tw-bg-black tw-bg-opacity-50 tw-flex tw-items-center tw-justify-center tw-z-50">
          <div className="tw-bg-white tw-rounded-lg tw-shadow-xl tw-max-w-2xl tw-w-full tw-m-4 tw-max-h-[90vh] tw-max-h-[90dvh] tw-flex tw-flex-col">
            <div className="tw-flex tw-items-center tw-justify-between tw-p-6 tw-border-b tw-flex-shrink-0">
              <h3 className="tw-text-xl tw-font-semibold tw-text-gray-900">Modifier la tarification</h3>
              <button
                onClick={() => setEditingPricing(false)}
                className="tw-text-gray-400 hover:tw-text-gray-600"
              >
                <X className="tw-w-5 tw-h-5" />
              </button>
            </div>

            <div className="tw-p-6 tw-space-y-4 tw-overflow-y-auto tw-flex-1 tw-overscroll-contain">
              {canMensualize && (
                <InlineSelectInput
                  label="Mode de paiement"
                  name="paymentMode"
                  options={[
                    { label: 'Comptant', value: 'cash_only' },
                    { label: 'Comptant ou mensualisation', value: 'both' },
                    { label: 'Mensualisation', value: 'monthly_only' },
                  ]}
                  value={formDataPricing.paymentMode}
                  onChange={(value) => setFormDataPricing({ ...formDataPricing, paymentMode: value as 'cash_only' | 'both' | 'monthly_only' })}
                  required
                  align="left"
                />
              )}

              <FormInput
                label={formDataPricing.paymentMode === 'monthly_only' ? 'Prix mensuel TTC (€)' : 'Prix de vente TTC (€)'}
                helpText={formDataPricing.paymentMode === 'monthly_only' ? 'Le prix saisi correspond au prix de vente mensuel (il sera multiplié par la durée pour obtenir le total).' : undefined}
                type="number"
                value={formDataPricing.sellingPrice.toString()}
                onChange={(value) => setFormDataPricing({ ...formDataPricing, sellingPrice: Number(value) })}
                required
                disabled={!permissions.canEditPrice}
              />

              {isInternalUser && (
                <FormInput
                  label="Prix de service Affinicar HT (€)"
                  type="number"
                  value={formDataPricing.servicePrice.toString()}
                  onChange={(value) => setFormDataPricing({ ...formDataPricing, servicePrice: Number(value) })}
                  required
                />
              )}
            </div>

            <div className="tw-flex tw-items-center tw-justify-end tw-gap-3 tw-p-6 tw-border-t tw-flex-shrink-0">
              <button
                onClick={() => setEditingPricing(false)}
                className="tw-px-4 tw-py-2 tw-text-gray-700 tw-bg-gray-100 tw-rounded-md hover:tw-bg-gray-200 tw-transition-colors"
              >
                Annuler
              </button>
              <button
                onClick={handleSubmitPricing}
                className="tw-px-4 tw-py-2 tw-text-white tw-rounded-md tw-transition-colors"
                style={{ backgroundColor: primaryColor }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.opacity = '0.9';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.opacity = '1';
                }}
              >
                Enregistrer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Confirmation Tarification */}
      {showConfirmPricing && (
        <div className="tw-fixed tw-inset-0 tw-bg-black tw-bg-opacity-50 tw-flex tw-items-center tw-justify-center tw-z-[101] tw-p-4">
          <div className="tw-bg-white tw-rounded-lg tw-shadow-xl tw-max-w-2xl tw-w-full tw-max-h-[80vh] tw-overflow-y-auto">
            <div className="tw-flex tw-items-center tw-justify-between tw-p-6 tw-border-b tw-border-gray-200">
              <div className="tw-flex tw-items-center tw-gap-3">
                <div className="tw-w-10 tw-h-10 tw-bg-affinicar-lighter tw-rounded-full tw-flex tw-items-center tw-justify-center">
                  <CheckCircle className="tw-w-6 tw-h-6 tw-text-affinicar-primary" />
                </div>
                <h2 className="tw-text-xl tw-font-bold tw-text-gray-900">
                  Confirmer les modifications
                </h2>
              </div>
              <button
                onClick={() => setShowConfirmPricing(false)}
                disabled={updateWarranty.isLoading}
                className="tw-text-gray-400 hover:tw-text-gray-600 tw-transition-colors disabled:tw-opacity-50"
              >
                <X className="tw-w-5 tw-h-5" />
              </button>
            </div>

            <div className="tw-p-6">
              <p className="tw-text-sm tw-text-gray-600 tw-mb-4">
                Vous êtes sur le point de modifier la tarification suivante :
              </p>

              <div className="tw-space-y-3">
                {pricingChanges.map((change, index) => (
                  <div
                    key={index}
                    className="tw-bg-gray-50 tw-border tw-border-gray-200 tw-rounded-lg tw-p-4"
                  >
                    <div className="tw-flex tw-items-start tw-gap-3">
                      <div className="tw-flex-1">
                        <p className="tw-text-sm tw-font-semibold tw-text-gray-900 tw-mb-2">
                          {change.label}
                        </p>
                        <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-3">
                          <div>
                            <p className="tw-text-xs tw-font-medium tw-text-gray-500 tw-mb-1">
                              Ancienne valeur :
                            </p>
                            <p className="tw-text-sm tw-text-gray-700 tw-bg-white tw-p-2 tw-rounded tw-border tw-border-gray-200">
                              {change.oldValue}
                            </p>
                          </div>
                          <div>
                            <p className="tw-text-xs tw-font-medium tw-text-green-600 tw-mb-1">
                              Nouvelle valeur :
                            </p>
                            <p className="tw-text-sm tw-text-green-700 tw-bg-green-50 tw-p-2 tw-rounded tw-border tw-border-green-200 tw-font-medium">
                              {change.newValue}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {errorPricing && (
                <div className="tw-bg-red-50 tw-border tw-border-red-200 tw-rounded-lg tw-p-4 tw-mt-4">
                  <div className="tw-flex tw-items-start tw-gap-3">
                    <AlertCircle className="tw-w-5 tw-h-5 tw-text-red-600 tw-flex-shrink-0 tw-mt-0.5" />
                    <p className="tw-text-sm tw-text-red-800">{errorPricing}</p>
                  </div>
                </div>
              )}
            </div>

            <div className="tw-flex tw-justify-end tw-gap-3 tw-p-6 tw-border-t tw-border-gray-200">
              <button
                type="button"
                onClick={() => setShowConfirmPricing(false)}
                disabled={updateWarranty.isLoading}
                className="tw-px-6 tw-py-2.5 tw-bg-gray-200 tw-text-gray-700 tw-font-medium tw-rounded-lg hover:tw-bg-gray-300 tw-transition-colors disabled:tw-opacity-50 disabled:tw-cursor-not-allowed"
              >
                Retour
              </button>
              <button
                type="button"
                onClick={handleConfirmPricing}
                disabled={updateWarranty.isLoading}
                className="tw-flex tw-items-center tw-gap-2 tw-px-6 tw-py-2.5 tw-text-white tw-font-medium tw-rounded-lg tw-transition-colors disabled:tw-opacity-50 disabled:tw-cursor-not-allowed"
                style={{ backgroundColor: primaryColor }}
                onMouseEnter={(e) => {
                  if (!updateWarranty.isLoading) {
                    e.currentTarget.style.opacity = '0.9';
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.opacity = '1';
                }}
              >
                {updateWarranty.isLoading ? (
                  <>
                    <Loader2 className="tw-w-4 tw-h-4 tw-animate-spin" />
                    Enregistrement...
                  </>
                ) : (
                  'Confirmer et enregistrer'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Édition Pièces Couvertes */}
      {editingParts && warranty && (
        <div className="tw-fixed tw-inset-0 tw-bg-black tw-bg-opacity-50 tw-flex tw-items-center tw-justify-center tw-z-50">
          <div className="tw-bg-white tw-rounded-lg tw-shadow-xl tw-max-w-6xl tw-w-full tw-m-4 tw-max-h-[90vh] tw-flex tw-flex-col">
            <div className="tw-flex tw-items-center tw-justify-between tw-p-6 tw-border-b tw-flex-shrink-0">
              <h3 className="tw-text-xl tw-font-semibold tw-text-gray-900">Modifier les pièces couvertes</h3>
              <button
                onClick={() => {
                  setEditingParts(false);
                  setExpandedCategories(new Set());
                }}
                className="tw-text-gray-400 hover:tw-text-gray-600"
              >
                <X className="tw-w-5 tw-h-5" />
              </button>
            </div>

            <div className="tw-p-6 tw-overflow-y-auto tw-flex-1">
              <p className="tw-text-sm tw-text-gray-600 tw-mb-4">
                {formDataParts.selectedPartIds.length} pièce{formDataParts.selectedPartIds.length > 1 ? 's' : ''} sélectionnée{formDataParts.selectedPartIds.length > 1 ? 's' : ''}
              </p>

              <div className="tw-space-y-2">
                {allCategories.map(category => (
                  <CategorySection
                    key={category.catKey}
                    categoryKey={category.catKey}
                    categoryLabel={category.label}
                    parts={category.parts}
                    vehicleType={warranty.vehicleType === 0 ? 'classic' : warranty.vehicleType === 1 ? 'collection' : 'two_wheeler'}
                    selectedPartIds={formDataParts.selectedPartIds}
                    isExpanded={expandedCategories.has(category.catKey)}
                    canModifyDefaultParts={permissions.canModifyDefaultParts}
                    onCategoryToggle={() => handleCategoryToggle(category.catKey)}
                    onPartToggle={handlePartToggle}
                    onSelectAllPriority={handleSelectAllPriority}
                  />
                ))}
              </div>
            </div>

            <div className="tw-flex tw-items-center tw-justify-end tw-gap-3 tw-p-6 tw-border-t tw-flex-shrink-0">
              <button
                onClick={() => {
                  setEditingParts(false);
                  setExpandedCategories(new Set());
                }}
                className="tw-px-4 tw-py-2 tw-text-gray-700 tw-bg-gray-100 tw-rounded-md hover:tw-bg-gray-200 tw-transition-colors"
              >
                Annuler
              </button>
              <button
                onClick={handleSubmitParts}
                className="tw-px-4 tw-py-2 tw-text-white tw-rounded-md tw-transition-colors"
                style={{ backgroundColor: primaryColor }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.opacity = '0.9';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.opacity = '1';
                }}
              >
                Enregistrer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Confirmation Pièces Couvertes */}
      {showConfirmParts && (
        <div className="tw-fixed tw-inset-0 tw-bg-black tw-bg-opacity-50 tw-flex tw-items-center tw-justify-center tw-z-[101] tw-p-4">
          <div className="tw-bg-white tw-rounded-lg tw-shadow-xl tw-max-w-2xl tw-w-full tw-max-h-[80vh] tw-overflow-y-auto">
            <div className="tw-flex tw-items-center tw-justify-between tw-p-6 tw-border-b tw-border-gray-200">
              <div className="tw-flex tw-items-center tw-gap-3">
                <div className="tw-w-10 tw-h-10 tw-bg-affinicar-lighter tw-rounded-full tw-flex tw-items-center tw-justify-center">
                  <CheckCircle className="tw-w-6 tw-h-6 tw-text-affinicar-primary" />
                </div>
                <h2 className="tw-text-xl tw-font-bold tw-text-gray-900">
                  Confirmer les modifications
                </h2>
              </div>
              <button
                onClick={() => setShowConfirmParts(false)}
                disabled={updateWarranty.isLoading}
                className="tw-text-gray-400 hover:tw-text-gray-600 tw-transition-colors disabled:tw-opacity-50"
              >
                <X className="tw-w-5 tw-h-5" />
              </button>
            </div>

            <div className="tw-p-6">
              <p className="tw-text-sm tw-text-gray-600 tw-mb-4">
                Vous êtes sur le point de modifier les pièces couvertes :
              </p>

              <div className="tw-space-y-4">
                {partsChanges.added.length > 0 && (
                  <div className="tw-bg-green-50 tw-border tw-border-green-200 tw-rounded-lg tw-p-4">
                    <p className="tw-text-sm tw-font-semibold tw-text-green-900 tw-mb-2">
                      Pièces ajoutées ({partsChanges.added.length})
                    </p>
                    <div className="tw-flex tw-flex-wrap tw-gap-2">
                      {partsChanges.added.map((partLabel, index) => (
                        <span
                          key={index}
                          className="tw-px-2 tw-py-1 tw-text-xs tw-bg-green-100 tw-text-green-800 tw-rounded tw-border tw-border-green-300"
                        >
                          {partLabel}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {partsChanges.removed.length > 0 && (
                  <div className="tw-bg-red-50 tw-border tw-border-red-200 tw-rounded-lg tw-p-4">
                    <p className="tw-text-sm tw-font-semibold tw-text-red-900 tw-mb-2">
                      Pièces retirées ({partsChanges.removed.length})
                    </p>
                    <div className="tw-flex tw-flex-wrap tw-gap-2">
                      {partsChanges.removed.map((partLabel, index) => (
                        <span
                          key={index}
                          className="tw-px-2 tw-py-1 tw-text-xs tw-bg-red-100 tw-text-red-800 tw-rounded tw-border tw-border-red-300"
                        >
                          {partLabel}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {errorParts && (
                <div className="tw-bg-red-50 tw-border tw-border-red-200 tw-rounded-lg tw-p-4 tw-mt-4">
                  <div className="tw-flex tw-items-start tw-gap-3">
                    <AlertCircle className="tw-w-5 tw-h-5 tw-text-red-600 tw-flex-shrink-0 tw-mt-0.5" />
                    <p className="tw-text-sm tw-text-red-800">{errorParts}</p>
                  </div>
                </div>
              )}
            </div>

            <div className="tw-flex tw-justify-end tw-gap-3 tw-p-6 tw-border-t tw-border-gray-200">
              <button
                type="button"
                onClick={() => setShowConfirmParts(false)}
                disabled={updateWarranty.isLoading}
                className="tw-px-6 tw-py-2.5 tw-bg-gray-200 tw-text-gray-700 tw-font-medium tw-rounded-lg hover:tw-bg-gray-300 tw-transition-colors disabled:tw-opacity-50 disabled:tw-cursor-not-allowed"
              >
                Retour
              </button>
              <button
                type="button"
                onClick={handleConfirmParts}
                disabled={updateWarranty.isLoading}
                className="tw-flex tw-items-center tw-gap-2 tw-px-6 tw-py-2.5 tw-text-white tw-font-medium tw-rounded-lg tw-transition-colors disabled:tw-opacity-50 disabled:tw-cursor-not-allowed"
                style={{ backgroundColor: primaryColor }}
                onMouseEnter={(e) => {
                  if (!updateWarranty.isLoading) {
                    e.currentTarget.style.opacity = '0.9';
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.opacity = '1';
                }}
              >
                {updateWarranty.isLoading ? (
                  <>
                    <Loader2 className="tw-w-4 tw-h-4 tw-animate-spin" />
                    Enregistrement...
                  </>
                ) : (
                  'Confirmer et enregistrer'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Confirmation Informations */}
      {showConfirmInfo && (
        <div className="tw-fixed tw-inset-0 tw-bg-black tw-bg-opacity-50 tw-flex tw-items-center tw-justify-center tw-z-[101] tw-p-4">
          <div className="tw-bg-white tw-rounded-lg tw-shadow-xl tw-max-w-2xl tw-w-full tw-max-h-[80vh] tw-overflow-y-auto">
            <div className="tw-flex tw-items-center tw-justify-between tw-p-6 tw-border-b tw-border-gray-200">
              <div className="tw-flex tw-items-center tw-gap-3">
                <div className="tw-w-10 tw-h-10 tw-bg-affinicar-lighter tw-rounded-full tw-flex tw-items-center tw-justify-center">
                  <CheckCircle className="tw-w-6 tw-h-6 tw-text-affinicar-primary" />
                </div>
                <h2 className="tw-text-xl tw-font-bold tw-text-gray-900">
                  Confirmer les modifications
                </h2>
              </div>
              <button
                onClick={() => setShowConfirmInfo(false)}
                disabled={updateWarranty.isLoading}
                className="tw-text-gray-400 hover:tw-text-gray-600 tw-transition-colors disabled:tw-opacity-50"
              >
                <X className="tw-w-5 tw-h-5" />
              </button>
            </div>

            <div className="tw-p-6">
              <p className="tw-text-sm tw-text-gray-600 tw-mb-4">
                Vous êtes sur le point de modifier les informations suivantes :
              </p>

              <div className="tw-space-y-3">
                {infoChanges.map((change, index) => (
                  <div
                    key={index}
                    className="tw-bg-gray-50 tw-border tw-border-gray-200 tw-rounded-lg tw-p-4"
                  >
                    <div className="tw-flex tw-items-start tw-gap-3">
                      <div className="tw-flex-1">
                        <p className="tw-text-sm tw-font-semibold tw-text-gray-900 tw-mb-2">
                          {change.label}
                        </p>
                        <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-3">
                          <div>
                            <p className="tw-text-xs tw-font-medium tw-text-gray-500 tw-mb-1">
                              Ancienne valeur :
                            </p>
                            <p className="tw-text-sm tw-text-gray-700 tw-bg-white tw-p-2 tw-rounded tw-border tw-border-gray-200">
                              {change.oldValue}
                            </p>
                          </div>
                          <div>
                            <p className="tw-text-xs tw-font-medium tw-text-green-600 tw-mb-1">
                              Nouvelle valeur :
                            </p>
                            <p className="tw-text-sm tw-text-green-700 tw-bg-green-50 tw-p-2 tw-rounded tw-border tw-border-green-200 tw-font-medium">
                              {change.newValue}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {errorInfo && (
                <div className="tw-bg-red-50 tw-border tw-border-red-200 tw-rounded-lg tw-p-4 tw-mt-4">
                  <div className="tw-flex tw-items-start tw-gap-3">
                    <AlertCircle className="tw-w-5 tw-h-5 tw-text-red-600 tw-flex-shrink-0 tw-mt-0.5" />
                    <p className="tw-text-sm tw-text-red-800">{errorInfo}</p>
                  </div>
                </div>
              )}
            </div>

            <div className="tw-flex tw-justify-end tw-gap-3 tw-p-6 tw-border-t tw-border-gray-200">
              <button
                type="button"
                onClick={() => setShowConfirmInfo(false)}
                disabled={updateWarranty.isLoading}
                className="tw-px-6 tw-py-2.5 tw-bg-gray-200 tw-text-gray-700 tw-font-medium tw-rounded-lg hover:tw-bg-gray-300 tw-transition-colors disabled:tw-opacity-50 disabled:tw-cursor-not-allowed"
              >
                Retour
              </button>
              <button
                type="button"
                onClick={handleConfirmInfo}
                disabled={updateWarranty.isLoading}
                className="tw-flex tw-items-center tw-gap-2 tw-px-6 tw-py-2.5 tw-text-white tw-font-medium tw-rounded-lg tw-transition-colors disabled:tw-opacity-50 disabled:tw-cursor-not-allowed"
                style={{ backgroundColor: primaryColor }}
                onMouseEnter={(e) => {
                  if (!updateWarranty.isLoading) {
                    e.currentTarget.style.opacity = '0.9';
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.opacity = '1';
                }}
              >
                {updateWarranty.isLoading ? (
                  <>
                    <Loader2 className="tw-w-4 tw-h-4 tw-animate-spin" />
                    Enregistrement...
                  </>
                ) : (
                  'Confirmer et enregistrer'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modale de confirmation changement de diffusion */}
      {showConfirmBroadcast && warranty && (
        <div className="tw-fixed tw-inset-0 tw-bg-black/60 tw-flex tw-items-center tw-justify-center tw-z-50 tw-p-4">
          <div className="tw-bg-white tw-rounded-2xl tw-shadow-2xl tw-w-full tw-max-w-md tw-overflow-hidden">
            <div className="tw-p-6 tw-border-b tw-border-gray-200">
              <div className="tw-flex tw-items-center tw-gap-3">
                <div className="tw-w-10 tw-h-10 tw-rounded-full tw-flex tw-items-center tw-justify-center" style={{ backgroundColor: `${primaryColor}15` }}>
                  {warranty.isBroadcast ? (
                    <EyeOff className="tw-w-5 tw-h-5" style={{ color: primaryColor }} />
                  ) : (
                    <Eye className="tw-w-5 tw-h-5" style={{ color: primaryColor }} />
                  )}
                </div>
                <div>
                  <h3 className="tw-text-lg tw-font-bold tw-text-gray-900 tw-mb-0">
                    {warranty.isBroadcast ? 'Retirer de la diffusion' : 'Remettre en diffusion'}
                  </h3>
                  <p className="tw-text-sm tw-text-gray-500 tw-mb-0">Confirmation requise</p>
                </div>
              </div>
            </div>

            <div className="tw-p-6">
              {warranty.isBroadcast ? (
                <p className="tw-text-sm tw-text-gray-600 tw-mb-0">
                  <strong>{warranty.name}</strong> disparaîtra de la liste {wording.desPlural},
                  de la création de contrat et du catalogue de l'API publique. Les contrats déjà
                  signés ne sont pas affectés. Cette page restera son seul point d'accès.
                </p>
              ) : (
                <p className="tw-text-sm tw-text-gray-600 tw-mb-0">
                  <strong>{warranty.name}</strong> deviendra sélectionnable à la création de contrat
                  et apparaîtra dans le catalogue de l'API publique. À ne faire que s'il s'agit
                  d'une offre du catalogue : une garantie sur mesure créée pour un contrat précis
                  doit rester non diffusée.
                </p>
              )}
            </div>

            <div className="tw-flex tw-justify-end tw-gap-3 tw-p-6 tw-border-t tw-border-gray-200">
              <button
                type="button"
                onClick={() => setShowConfirmBroadcast(false)}
                disabled={broadcastWarranty.isLoading}
                className="tw-px-6 tw-py-2.5 tw-bg-gray-200 tw-text-gray-700 tw-font-medium tw-rounded-lg hover:tw-bg-gray-300 tw-transition-colors disabled:tw-opacity-50 disabled:tw-cursor-not-allowed"
              >
                Annuler
              </button>
              <button
                type="button"
                onClick={handleToggleBroadcast}
                disabled={broadcastWarranty.isLoading}
                className="tw-flex tw-items-center tw-gap-2 tw-px-6 tw-py-2.5 tw-text-white tw-font-medium tw-rounded-lg tw-transition-colors disabled:tw-opacity-50 disabled:tw-cursor-not-allowed"
                style={{ backgroundColor: primaryColor }}
              >
                {broadcastWarranty.isLoading && <Loader2 className="tw-w-4 tw-h-4 tw-animate-spin" />}
                Confirmer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modale de confirmation mise à jour des provisions */}
      {showConfirmProvisions && warranty && (
        <div className="tw-fixed tw-inset-0 tw-bg-black/60 tw-flex tw-items-center tw-justify-center tw-z-50 tw-p-4">
          <div className="tw-bg-white tw-rounded-2xl tw-shadow-2xl tw-w-full tw-max-w-md tw-overflow-hidden">
            <div className="tw-p-6 tw-border-b tw-border-gray-200">
              <div className="tw-flex tw-items-center tw-gap-3">
                <div className="tw-w-10 tw-h-10 tw-rounded-full tw-flex tw-items-center tw-justify-center" style={{ backgroundColor: `${primaryColor}15` }}>
                  <RefreshCw className="tw-w-5 tw-h-5" style={{ color: primaryColor }} />
                </div>
                <div>
                  <h3 className="tw-text-lg tw-font-bold tw-text-gray-900 tw-mb-0">Mettre à jour les provisions</h3>
                  <p className="tw-text-sm tw-text-gray-500 tw-mb-0">Confirmation requise</p>
                </div>
              </div>
            </div>

            <div className="tw-p-6">
              <p className="tw-text-sm tw-text-gray-600 tw-mb-4">
                Les contrats <strong>actifs et expirés</strong> liés à cette garantie seront mis à jour
                avec la provision actuelle. Les contrats annulés conservent leur provision d'origine.
              </p>
              <div className="tw-bg-gray-50 tw-rounded-lg tw-p-4 tw-text-center">
                <span className="tw-text-2xl tw-font-bold" style={{ color: primaryColor }}>
                  {formatPrice(warranty.provision ?? 0)}
                </span>
              </div>
            </div>

            <div className="tw-flex tw-justify-end tw-gap-3 tw-p-6 tw-border-t tw-border-gray-200">
              <button
                type="button"
                onClick={() => setShowConfirmProvisions(false)}
                disabled={updateProvisions.isLoading}
                className="tw-px-6 tw-py-2.5 tw-bg-gray-200 tw-text-gray-700 tw-font-medium tw-rounded-lg hover:tw-bg-gray-300 tw-transition-colors disabled:tw-opacity-50 disabled:tw-cursor-not-allowed"
              >
                Annuler
              </button>
              <button
                type="button"
                onClick={() => {
                  updateProvisions.mutate(warranty.uuid, {
                    onSuccess: (data) => {
                      setShowConfirmProvisions(false);
                      showNotification('success', `${data.total} contrat(s) mis à jour avec succès`);
                    },
                    onError: (err: any) => {
                      showNotification('error', err?.message || 'Erreur lors de la mise à jour des provisions');
                    },
                  });
                }}
                disabled={updateProvisions.isLoading}
                className="tw-flex tw-items-center tw-gap-2 tw-px-6 tw-py-2.5 tw-text-white tw-font-medium tw-rounded-lg tw-transition-colors disabled:tw-opacity-50 disabled:tw-cursor-not-allowed"
                style={{ backgroundColor: primaryColor }}
                onMouseEnter={(e) => {
                  if (!updateProvisions.isLoading) {
                    e.currentTarget.style.opacity = '0.9';
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.opacity = '1';
                }}
              >
                {updateProvisions.isLoading ? (
                  <>
                    <Loader2 className="tw-w-4 tw-h-4 tw-animate-spin" />
                    Mise à jour...
                  </>
                ) : (
                  'Confirmer la mise à jour'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modale de confirmation propagation des informations complémentaires */}
      {showConfirmPropagateDiagnostic && warranty && warranty.diagnostic && (
        <div className="tw-fixed tw-inset-0 tw-bg-black/60 tw-flex tw-items-center tw-justify-center tw-z-50 tw-p-4">
          <div className="tw-bg-white tw-rounded-2xl tw-shadow-2xl tw-w-full tw-max-w-lg tw-overflow-hidden">
            <div className="tw-p-6 tw-border-b tw-border-gray-200">
              <div className="tw-flex tw-items-center tw-gap-3">
                <div className="tw-w-10 tw-h-10 tw-rounded-full tw-bg-amber-100 tw-flex tw-items-center tw-justify-center">
                  <AlertCircle className="tw-w-5 tw-h-5 tw-text-amber-600" />
                </div>
                <div>
                  <h3 className="tw-text-lg tw-font-bold tw-text-gray-900 tw-mb-0">
                    Propager à toutes les garanties
                  </h3>
                  <p className="tw-text-sm tw-text-gray-500 tw-mb-0">Confirmation requise</p>
                </div>
              </div>
            </div>

            <div className="tw-p-6">
              <p className="tw-text-sm tw-text-gray-600 tw-mb-3">
                Toutes les autres garanties de la société{society?.name ? <> <strong>{society.name}</strong></> : null} verront leur champ
                <em> « Informations complémentaires »</em> remplacé par le texte ci-dessous.
                Cette action est irréversible.
              </p>
              <div className="tw-text-xs tw-text-gray-800 tw-bg-gray-50 tw-p-3 tw-rounded tw-whitespace-pre-wrap tw-border tw-border-gray-200 tw-max-h-48 tw-overflow-y-auto">
                {warranty.diagnostic}
              </div>

              {errorPropagateDiagnostic && (
                <div className="tw-bg-red-50 tw-border tw-border-red-200 tw-rounded-lg tw-p-3 tw-mt-4">
                  <div className="tw-flex tw-items-start tw-gap-2">
                    <AlertCircle className="tw-w-4 tw-h-4 tw-text-red-600 tw-flex-shrink-0 tw-mt-0.5" />
                    <p className="tw-text-sm tw-text-red-800 tw-mb-0">{errorPropagateDiagnostic}</p>
                  </div>
                </div>
              )}
            </div>

            <div className="tw-flex tw-justify-end tw-gap-3 tw-p-6 tw-border-t tw-border-gray-200">
              <button
                type="button"
                onClick={() => setShowConfirmPropagateDiagnostic(false)}
                disabled={propagateDiagnostic.isLoading}
                className="tw-px-6 tw-py-2.5 tw-bg-gray-200 tw-text-gray-700 tw-font-medium tw-rounded-lg hover:tw-bg-gray-300 tw-transition-colors disabled:tw-opacity-50 disabled:tw-cursor-not-allowed"
              >
                Annuler
              </button>
              <button
                type="button"
                onClick={() => {
                  setErrorPropagateDiagnostic(null);
                  propagateDiagnostic.mutate(warranty.uuid, {
                    onSuccess: (data) => {
                      setShowConfirmPropagateDiagnostic(false);
                      showNotification(
                        'success',
                        `${data.updatedCount} garantie(s) mise(s) à jour`,
                      );
                    },
                    onError: (err: any) => {
                      setErrorPropagateDiagnostic(
                        err?.response?.data?.message || err?.message || 'Erreur lors de la propagation',
                      );
                    },
                  });
                }}
                disabled={propagateDiagnostic.isLoading}
                className="tw-flex tw-items-center tw-gap-2 tw-px-6 tw-py-2.5 tw-bg-amber-600 tw-text-white tw-font-medium tw-rounded-lg hover:tw-bg-amber-700 tw-transition-colors disabled:tw-opacity-50 disabled:tw-cursor-not-allowed"
              >
                {propagateDiagnostic.isLoading ? (
                  <>
                    <Loader2 className="tw-w-4 tw-h-4 tw-animate-spin" />
                    Propagation...
                  </>
                ) : (
                  'Confirmer la propagation'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modale Brochure avant vente */}
      {showPresalePanel && warranty && (
        <PresaleSheetPanel
          key={warranty.uuid}
          warrantyUuid={warranty.uuid}
          warrantyName={warranty.name}
          onClose={() => setShowPresalePanel(false)}
        />
      )}

    </div>
  );
};
